import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  IconButton,
  Divider,
  Tab,
  Tabs,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  alpha,
  Collapse,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Popover,
  SvgIcon,
  Badge,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  LinearProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import UploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FlightIcon from '@mui/icons-material/Flight';
import CameraIcon from '@mui/icons-material/Camera';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import BugReportIcon from '@mui/icons-material/BugReport';
import CategoryIcon from '@mui/icons-material/Category';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoIcon from '@mui/icons-material/Info';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import HelpIcon from '@mui/icons-material/Help';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import GroupIcon from '@mui/icons-material/Group';
import DnsIcon from '@mui/icons-material/Dns';
import HubIcon from '@mui/icons-material/Hub';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DoneIcon from '@mui/icons-material/Done';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PDFReport from './PDFReport'; // Add this import
import AuthenticatedImage from '../../common/AuthenticatedImage'; // Import the new component
// Import authenticated API service instead of raw axios
import { default as api } from '../../../services/api';
import { getAiServerStatus, getAiModelDetails } from '../../../services/aiService'; // Import AI service
import { useAuth } from '../../../hooks/auth/useAuth'; // Import useAuth hook

// Cards per row icon
const GridDensityIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M4 5v13h17V5H4zm15 11h-6v-9h6v9zm-8 0H6v-9h5v9z" />
  </SvgIcon>
);

const InspectionDetailsModal = ({ open, onClose, inspection, onPhotoSelect, onDataMutated, fullScreenMode = false }) => {
  const theme = useTheme();
  const { user } = useAuth(); // Get current user from auth context
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [galleryViewMode, setGalleryViewMode] = useState('card'); // Default to card view
  const [cardsPerRow, setCardsPerRow] = useState(3);
  const [densityMenuAnchor, setDensityMenuAnchor] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Multi-photo selection for deletion
  const [selectionModeForDelete, setSelectionModeForDelete] = useState(false);
  const [selectedPhotosForDelete, setSelectedPhotosForDelete] = useState({});
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0 });
  const [defectTypes, setDefectTypes] = useState([]);
  const [defectCounts, setDefectCounts] = useState({});
  const [defectTypesLoading, setDefectTypesLoading] = useState(false);
  const [defectSummaryExpanded, setDefectSummaryExpanded] = useState(false);
  const [selectedDefectTypeId, setSelectedDefectTypeId] = useState(null);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  // Add state for image selection mode in AI analysis
  const [selectingImages, setSelectingImages] = useState(false);
  const [selectedImages, setSelectedImages] = useState({});
  // Add state for report view
  const [reportView, setReportView] = useState(false);
  // Add state for AI analysis view
  const [aiAnalysisView, setAiAnalysisView] = useState(false);
  // Add state for defect report data
  const [defectReport, setDefectReport] = useState({
    loading: false,
    error: null,
    data: null
  });
  // Track which defects the user has UNTICKED in the Findings Review list.
  // Stored as a Set of stringified defect IDs (works for both numeric defect ids and "draft-<n>" ids).
  // Default empty = all defects selected.
  const [excludedDefectIds, setExcludedDefectIds] = useState(() => new Set());
  // Add state for severity levels
  const [severityLevels, setSeverityLevels] = useState([]);
  // Add a new state variable to track report preview mode
  const [reportPreviewMode, setReportPreviewMode] = useState(false);
  // Add state to track draft preview mode
  const [draftPreviewMode, setDraftPreviewMode] = useState(false);
  // Report date - user-selectable date shown on cover page
  const [reportDate, setReportDate] = useState(new Date());
  // Add state to hold the current inspection data (might be updated)
  const [currentInspection, setCurrentInspection] = useState(null);
  // Add state for AI analyzing animation
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzedImages, setAnalyzedImages] = useState({});
  const [aiServerStatus, setAiServerStatus] = useState(null);
  const [aiModelDetails, setAiModelDetails] = useState(null);
  const [aiDataLoading, setAiDataLoading] = useState(false);
  const [aiDataError, setAiDataError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [currentAssetType, setCurrentAssetType] = useState(null);
  const [aiJob, setAiJob] = useState({ 
    jobs: [], // Array of batch jobs: { job_id, batch_number, total_batches, batch_size, status, progress, results }
    currentBatchIndex: 0,
    status: 'IDLE', // IDLE, SUBMITTING, PENDING, PROGRESS, SUCCESS, FAILED, PARTIAL_SUCCESS
    overallProgress: null,
    error: null,
    failedBatches: [],
    allResults: [] // Accumulated results from all batches
  });
  // Add state for AI annotation warning dialog
  const [aiWarningDialogOpen, setAiWarningDialogOpen] = useState(false);
  const [imagesWithAiAnnotations, setImagesWithAiAnnotations] = useState([]);
  // Add state for rating functionality
  const [availableRatings, setAvailableRatings] = useState([]);
  const [selectedRating, setSelectedRating] = useState('');
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingUpdateLoading, setRatingUpdateLoading] = useState(false);
  // Add state for selected AI model
  const [selectedModel, setSelectedModel] = useState(null);
  // Add state for asset details for report summary
  const [assetDetails, setAssetDetails] = useState(null);
  
  // Fetch fresh inspection data when modal opens to ensure we have the latest rating info
  useEffect(() => {
    if (!open || !inspection?.id) {
      setCurrentInspection(null);
      return;
    }

    const fetchFreshInspectionData = async () => {
      try {
        console.log('[InspectionDetailsModal] Fetching fresh inspection data for ID:', inspection.id);
        const response = await api.get(`/inspections/${inspection.id}`);
        
        if (response.data) {
          const freshInspection = response.data;
          setCurrentInspection(freshInspection);
          console.log('[InspectionDetailsModal] Fresh inspection data:', {
            id: freshInspection.id,
            condition_rating_level_id: freshInspection.condition_rating_level_id,
            status: freshInspection.status
          });
          
          // Set rating from fresh data
          if (freshInspection.condition_rating_level_id) {
            const ratingId = String(freshInspection.condition_rating_level_id);
            setSelectedRating(ratingId);
            console.log('[InspectionDetailsModal] Set rating from fresh data:', freshInspection.condition_rating_level_id, '-> as string:', ratingId);
          } else {
            setSelectedRating('');
            console.log('[InspectionDetailsModal] No rating found in fresh data, clearing selection');
          }
        }
      } catch (error) {
        console.error('[InspectionDetailsModal] Error fetching fresh inspection data:', error);
        // Fallback to original inspection data
        setCurrentInspection(inspection);
        
        if (inspection.condition_rating_level_id) {
          const ratingId = String(inspection.condition_rating_level_id);
          setSelectedRating(ratingId);
          console.log('[InspectionDetailsModal] Fallback: Set rating from original inspection:', inspection.condition_rating_level_id, '-> as string:', ratingId);
        } else {
          setSelectedRating('');
          console.log('[InspectionDetailsModal] Fallback: No rating found in original inspection, clearing selection');
        }
      }
    };

    fetchFreshInspectionData();
  }, [open, inspection?.id]);

  // Debug: Log when availableRatings and selectedRating change
  useEffect(() => {
    console.log('[InspectionDetailsModal] availableRatings updated:', availableRatings.length);
    console.log('[InspectionDetailsModal] selectedRating state:', selectedRating);
    if (selectedRating && availableRatings.length > 0) {
      const found = availableRatings.find(r => String(r.id) === String(selectedRating));
      console.log('[InspectionDetailsModal] Found matching rating:', found);
    }
  }, [availableRatings, selectedRating]);

  // Additional safety net: Re-sync rating when availableRatings load if we have an inspection rating
  useEffect(() => {
    if (availableRatings.length > 0 && currentInspection?.condition_rating_level_id && !selectedRating) {
      const ratingId = String(currentInspection.condition_rating_level_id);
      setSelectedRating(ratingId);
      console.log('[InspectionDetailsModal] Safety net: Re-syncing rating after availableRatings loaded:', ratingId);
    }
  }, [availableRatings.length, currentInspection?.condition_rating_level_id, selectedRating]);
  
  // Auto-select model when asset type and AI models are available
  useEffect(() => {
    if (aiModelDetails?.models && currentAssetType && !selectedModel) {
      const matchingModels = aiModelDetails.models.filter(model =>
        model.model_type === currentAssetType
      );
      if (matchingModels.length > 0) {
        // Sort by version descending to get the latest model
        const sortedModels = [...matchingModels].sort((a, b) => {
          const parseVersion = (version) => {
            if (!version) return [0, 0, 0];
            const parts = String(version).split('.').map(Number);
            return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
          };
          const [aMajor, aMinor, aPatch] = parseVersion(a.model_version);
          const [bMajor, bMinor, bPatch] = parseVersion(b.model_version);
          // Compare major, then minor, then patch (descending)
          if (bMajor !== aMajor) return bMajor - aMajor;
          if (bMinor !== aMinor) return bMinor - aMinor;
          return bPatch - aPatch;
        });
        setSelectedModel(sortedModels[0]); // Auto-select latest version
      }
    }
  }, [aiModelDetails, currentAssetType, selectedModel]);
  
  // Function to fetch AI server data
  const fetchAiData = async () => {
    setAiDataLoading(true);
    setAiDataError(null);
    try {
      const [status, models] = await Promise.all([
        getAiServerStatus(),
        getAiModelDetails(),
      ]);
      setAiServerStatus(status);
      setAiModelDetails(models);
      setLastRefreshTime(new Date());
    } catch (err) {
      setAiDataError('Failed to connect to AI server.');
    } finally {
      setAiDataLoading(false);
    }
  };
  
  // Toggle drone data expansion for a specific photo
  const toggleDroneData = (photoId) => {
    setExpandedCards(prev => ({
      ...prev,
      [photoId]: !prev[photoId]
    }));
  };

  // Fetch inspection photos when inspection is selected
  useEffect(() => {
    if (!open || !currentInspection) return;
    
    const fetchPhotos = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`Fetching photos for inspection ${currentInspection.id}`);
        const response = await api.get(`/inspections/${currentInspection.id}/photos?include_annotations=true`);
        
        if (Array.isArray(response.data)) {
          // Create proper URLs for display
          const photosWithUrls = response.data.map(photo => {
            // The backend now provides a clean, web-relative path like 'uploads/11/inspections/123/photo.jpg'
            // We just need to prepend the API endpoint.
            const photoPath = photo.photo_path.replace(/\\/g, '/');
            
            return {
              ...photo,
              photo_path: photoPath,
              url: photoPath,
              has_annotation: photo.has_annotation || false,
              annotation_count: photo.annotation_count || 0,
              defect_types: [] // Initialize empty defect types array
            };
          });
          
          setPhotos(photosWithUrls);
          console.log('Photos with URLs:', photosWithUrls);
          
          // Fetch defect types for each photo
          const photoIds = photosWithUrls.map(p => p.id).filter(id => id);
          if (photoIds.length > 0) {
            try {
              // Process in batches to avoid too many concurrent requests
              const batchSize = 5;
              const photoDefectMap = {};
              
              for (let i = 0; i < photoIds.length; i += batchSize) {
                const batch = photoIds.slice(i, i + batchSize);
                const batchPromises = batch.map(photoId => 
                  api.get(`/bounding-boxes`, {
                    params: {
                      photo_id: photoId
                    }
                  })
                );
                
                const responses = await Promise.all(batchPromises);
                
                // Process bounding boxes to extract defect types for each photo
                responses.forEach((response, index) => {
                  const photoId = batch[index];
                  if (Array.isArray(response.data)) {
                    // Extract unique defect types for this photo
                    const defectTypes = [...new Set(
                      response.data
                        .filter(box => box.defect_type_id)
                        .map(box => parseInt(box.defect_type_id, 10))
                    )];
                    photoDefectMap[photoId] = defectTypes;
                  }
                });
              }
              
              // Update photos with defect type information
              setPhotos(prevPhotos => 
                prevPhotos.map(photo => ({
                  ...photo,
                  defect_types: photoDefectMap[photo.id] || []
                }))
              );
            } catch (err) {
              console.error('Error fetching defect types for photos:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching photos:', err);
        let errorMessage = 'Failed to load inspection photos';
        
        if (err.response) {
          errorMessage += `: ${err.response.status} - ${err.response.data?.error || ''}`;
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPhotos();
    // Reset state when modal opens
    setTabValue(0);
    setUploadFiles([]);
    setUploadProgress(0);
    setUploadError(null);
    setSuccess(false);
    setExpandedCards({});
    setSelectedDefectTypeId(null);
  }, [open, currentInspection]);
  
  // Fetch defect types and counts for this inspection
  useEffect(() => {
    if (!open || !currentInspection) return;
    
    const fetchDefectTypesAndCounts = async () => {
      console.log('[InspectionDetailsModal] fetchDefectTypesAndCounts called. Inspection object:', JSON.stringify(currentInspection, null, 2));
      setDefectTypesLoading(true);
      let localCountsByType = {}; // Use a local variable for accumulating counts
      // Track which defect types each photo has
      const photoDefectTypes = {};

      try {
        let assetTypeId = null;
        let organizationId = null;
        
        if (currentInspection.asset_id) {
          try {
            console.log(`[InspectionDetailsModal] Found inspection.asset_id: ${currentInspection.asset_id}. Fetching asset details.`);
            const assetResponse = await api.get(`/assets/${currentInspection.asset_id}`);
            if (assetResponse.data) {
              console.log('[InspectionDetailsModal] Raw assetResponse.data:', JSON.stringify(assetResponse.data, null, 2));
              // Store full asset details for report summary
              setAssetDetails(assetResponse.data);
              
              assetTypeId = assetResponse.data.general_type_id;
              organizationId = assetResponse.data.organization_id;
              // Prefer asset type name from asset response if available
              if (assetResponse.data.type_name) {
                setCurrentAssetType(assetResponse.data.type_name);
              }
              // Fallback to previous method if type_name not present
              console.log(`[InspectionDetailsModal] Asset details extracted - general_type_id: ${assetTypeId}, organization ID: ${organizationId}`);
              
              // If we still don't have the asset type name, fetch from asset-types list
              if (assetTypeId && !assetResponse.data.type_name && !currentAssetType) {
                try {
                  const listResponse = await api.get(`/asset-types`);
                  if (Array.isArray(listResponse.data)) {
                    const match = listResponse.data.find(t => t.id === assetTypeId);
                    if (match) {
                      setCurrentAssetType(match.name);
                      console.log(`[InspectionDetailsModal] Asset type name (via list lookup): ${match.name}`);
                    }
                  }
                } catch (typeErr) {
                  console.error('[InspectionDetailsModal] Error fetching asset types list:', typeErr.message);
                }
              }
            } else {
              console.log('[InspectionDetailsModal] assetResponse.data is null or undefined after fetching asset details.');
            }
          } catch (assetErr) {
            console.error('[InspectionDetailsModal] Error fetching asset details for asset_id '+ inspection.asset_id + ':', assetErr.message);
          }
        } else if (inspection.organization_id) {
          organizationId = inspection.organization_id;
          console.log(`[InspectionDetailsModal] Used inspection.organization_id: ${organizationId} (inspection.asset_id was not found). AssetTypeID will be null.`);
        } else {
          console.log('[InspectionDetailsModal] Neither inspection.asset_id nor inspection.organization_id was found on the inspection object.');
        }
        
        // --- Fetch Severity Levels --- START
        if (organizationId && assetTypeId) {
          try {
            console.log(`[InspectionDetailsModal] Fetching severity scheme for org ${organizationId}, asset type ${assetTypeId}`);
            const severityResponse = await api.get(`/severity-scheme-for-asset`, {
              params: { 
                organization_id: organizationId,
                asset_type_id: assetTypeId
              }
            });
            if (severityResponse.data && severityResponse.data.levels) {
              setSeverityLevels(severityResponse.data.levels);
              console.log(`[InspectionDetailsModal] Found ${severityResponse.data.levels.length} severity levels.`);
            } else {
              console.log('[InspectionDetailsModal] No severity levels found for this scheme.');
              setSeverityLevels([]); // Clear if none found
            }
          } catch (severityErr) {
            console.error('[InspectionDetailsModal] Error fetching severity scheme:', severityErr.message);
            setSeverityLevels([]); // Clear on error
          }
        } else {
          console.log('[InspectionDetailsModal] Skipping severity fetch: Missing organizationId or assetTypeId.');
          setSeverityLevels([]); // Clear if we can't fetch
        }
        // --- Fetch Severity Levels --- END
        
        // --- Fetch Rating Levels --- START
        if (organizationId && assetTypeId) {
          try {
            setRatingsLoading(true);
            console.log(`[InspectionDetailsModal] Fetching rating scheme for org ${organizationId}, asset type ${assetTypeId}`);
            const ratingResponse = await api.get(`/ratings/rating-scheme-for-asset`, {
              params: { 
                organization_id: organizationId,
                asset_type_id: assetTypeId
              }
            });
            if (ratingResponse.data && ratingResponse.data.levels) {
              setAvailableRatings(ratingResponse.data.levels);
              console.log(`[InspectionDetailsModal] Found ${ratingResponse.data.levels.length} rating levels`);
            } else {
              console.log('[InspectionDetailsModal] No rating levels found for this scheme.');
              setAvailableRatings([]); // Clear if none found
            }
          } catch (ratingErr) {
            console.error('[InspectionDetailsModal] Error fetching rating scheme:', ratingErr.message);
            setAvailableRatings([]); // Clear on error
          } finally {
            setRatingsLoading(false);
          }
        } else {
          console.log('[InspectionDetailsModal] Skipping rating fetch: Missing organizationId or assetTypeId');
          setAvailableRatings([]); // Clear if we can't fetch
          setRatingsLoading(false);
        }
        // --- Fetch Rating Levels --- END
        
        let defectTypesData = [];
        
        try {
          const params = {};
          
          if (assetTypeId) {
            params.asset_type_id = assetTypeId;
          } else {
            console.log('[InspectionDetailsModal] general_type_id is null or undefined. Not adding asset_type_id to defect-types params.');
          }
          
          if (organizationId) {
            params.organization_id = organizationId;
          } else {
            console.log('[InspectionDetailsModal] organizationId is null or undefined. Not adding organization_id to defect-types params.');
          }
          
          if (Object.keys(params).length === 0) {
            console.log('[InspectionDetailsModal] No filters (asset_type_id or organization_id) will be applied to defect-types fetch as both are null/undefined.');
          }
          
          console.log('[InspectionDetailsModal] Calling /defect-types endpoint with params:', JSON.stringify(params));
          const typesResponse = await api.get('/defect-types', { params });
          
          if (Array.isArray(typesResponse.data)) {
            defectTypesData = typesResponse.data;
            console.log(`Found ${defectTypesData.length} defect types for general_type_id ${assetTypeId} and organization ${organizationId}`);
          }
        } catch (typesErr) {
          console.error('Error fetching from defect-types endpoint:', typesErr);
          
          // FALLBACK: If the classification endpoint fails, try direct database query via defects
          try {
            console.log('Attempting fallback defect types fetch via defects API...');
            // Query the all defects to extract unique defect types, filtering by asset type and organization
            const defectsParams = {};
            if (assetTypeId) defectsParams.asset_type_id = assetTypeId;
            if (organizationId) defectsParams.organization_id = organizationId;
            
            const defectsResponse = await api.get(`/defects`, { params: defectsParams });
            
            if (Array.isArray(defectsResponse.data)) {
              // Extract unique defect types from defects response, filtering by asset type and organization
              const uniqueTypes = {};
              defectsResponse.data.forEach(defect => {
                // Only include defects matching our filters
                let matchesFilters = true;
                if (assetTypeId && defect.asset_type_id !== assetTypeId) matchesFilters = false;
                if (organizationId && defect.organization_id !== organizationId) matchesFilters = false;
                
                if (matchesFilters && defect.defect_type_id && defect.defect_type_name) {
                  uniqueTypes[defect.defect_type_id] = {
                    id: defect.defect_type_id,
                    name: defect.defect_type_name,
                    asset_type_id: defect.asset_type_id,
                    organization_id: defect.organization_id
                  };
                }
              });
              defectTypesData = Object.values(uniqueTypes);
              console.log(`Fallback: Found ${defectTypesData.length} defect types from defects API with filters`);
            }
          } catch (fallbackErr) {
            console.error('Fallback defect types fetch also failed:', fallbackErr);
          }
        }
        
        // HARDCODED FALLBACK: If we still have no defect types but we know the asset is a bridge (type 5)
        // Only apply this fallback if we know the asset type is 5 (bridge)
        if (defectTypesData.length === 0 && assetTypeId === 5) {
          console.log('Using hardcoded bridge defect types as final fallback');
          defectTypesData = [
            { id: 1, name: 'spalling', asset_type_id: 5, organization_id: organizationId },
            { id: 2, name: 'spalling exposing rebar', asset_type_id: 5, organization_id: organizationId },
            { id: 3, name: 'vegetation', asset_type_id: 5, organization_id: organizationId },
            { id: 4, name: 'bridge joint', asset_type_id: 5, organization_id: organizationId },
            { id: 5, name: 'rust staining', asset_type_id: 5, organization_id: organizationId },
            { id: 6, name: 'cracks', asset_type_id: 5, organization_id: organizationId },
            { id: 7, name: 'drainage defect', asset_type_id: 5, organization_id: organizationId },
            { id: 8, name: 'staining', asset_type_id: 5, organization_id: organizationId },
            { id: 9, name: 'mould growth', asset_type_id: 5, organization_id: organizationId }
          ];
        }
        
        setDefectTypes(prevDefectTypes => {
          if (JSON.stringify(prevDefectTypes) === JSON.stringify(defectTypesData)) return prevDefectTypes;
          return defectTypesData;
        });
        
        // Get photo IDs for this inspection to fetch bounding boxes
        const photoIds = photos.map(photo => photo.id).filter(id => id);
        
        const countsByPhotoId = {};
        
        if (photoIds.length > 0) {
          try {
            // Fetch bounding boxes for each photo individually instead of passing an array
            const batchSize = 5;
            for (let i = 0; i < photoIds.length; i += batchSize) {
              const batch = photoIds.slice(i, i + batchSize);
              const batchPromises = batch.map(photoId => 
                api.get(`/bounding-boxes`, {
                  params: {
                    photo_id: photoId
                  }
                })
              );
              
              const responses = await Promise.all(batchPromises);
              
              // Count bounding boxes for each photo
              responses.forEach((response, index) => {
                const photoId = batch[index];
                if (Array.isArray(response.data)) {
                  countsByPhotoId[photoId] = response.data.length;
                  
                  // Add to defect type counts and track defect types per photo
                  photoDefectTypes[photoId] = [];
                  
                  response.data.forEach(box => {
                    if (box.defect_type_id) {
                      // Make sure we're using integers for keys
                      const defectTypeId = parseInt(box.defect_type_id, 10);
                      localCountsByType[defectTypeId] = (localCountsByType[defectTypeId] || 0) + 1;
                      
                      // Add this defect type to the photo's list if not already there
                      if (!photoDefectTypes[photoId].includes(defectTypeId)) {
                        photoDefectTypes[photoId].push(defectTypeId);
                      }
                    }
                  });
                }
              });
            }
            
            // Update photos with the annotation counts and defect types
            setPhotos(prevPhotos => {
              let photosChanged = false;
              const updatedPhotos = prevPhotos.map(photo => {
                const newCount = countsByPhotoId[photo.id] !== undefined ? countsByPhotoId[photo.id] : 0;
                const newHasAnno = newCount > 0;
                const newDefectTypes = photoDefectTypes[photo.id] || [];
                
                const currentCount = typeof photo.annotation_count === 'number' ? photo.annotation_count : 0;
                const currentHasAnno = typeof photo.has_annotation === 'boolean' ? photo.has_annotation : false;
                
                if (currentCount !== newCount || 
                    currentHasAnno !== newHasAnno || 
                    !photo.defect_types ||
                    JSON.stringify(photo.defect_types) !== JSON.stringify(newDefectTypes)) {
                  photosChanged = true;
                  return { 
                    ...photo, 
                    annotation_count: newCount, 
                    has_annotation: newHasAnno,
                    defect_types: newDefectTypes
                  };
                }
                return photo;
              });
              return photosChanged ? updatedPhotos : prevPhotos;
            });
            
            console.log('Bounding box counts by defect type:', localCountsByType);
          } catch (boxError) {
            console.error('Error fetching bounding boxes:', boxError);
          }
        }
        
        setDefectCounts(prevDefectCounts => {
          const newKeys = Object.keys(localCountsByType);
          const prevKeys = Object.keys(prevDefectCounts);
          if (newKeys.length === prevKeys.length && newKeys.every(key => prevDefectCounts[key] === localCountsByType[key])) {
            return prevDefectCounts;
          }
          return { ...localCountsByType };
        });
        
      } catch (err) {
        console.error('Error in defect types and counts fetch process:', err);
      } finally {
        setDefectTypesLoading(false);
      }
    };
    
    // Only fetch if we have inspection data
    if (currentInspection?.id) {
      fetchDefectTypesAndCounts();
    }
  }, [open, currentInspection, photos]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle gallery view mode change
  const handleGalleryViewChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setGalleryViewMode(newViewMode);
    }
  };
  
  // Dropzone configuration
  const onDrop = useCallback(acceptedFiles => {
    // Add preview to files
    const filesWithPreview = acceptedFiles.map(file => 
      Object.assign(file, {
        preview: URL.createObjectURL(file)
      })
    );
    
    setUploadFiles(prev => [...prev, ...filesWithPreview]);
    setUploadError(null);
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxSize: 20971520 // 20MB
  });
  
  // Clean up previews when component unmounts
  useEffect(() => {
    return () => {
      uploadFiles.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [uploadFiles]);
  
  // Remove file from upload list
  const removeFile = (fileIndex) => {
    setUploadFiles(prevFiles => {
      const newFiles = [...prevFiles];
      const fileToRemove = newFiles[fileIndex];
      if (fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      newFiles.splice(fileIndex, 1);
      return newFiles;
    });
  };
  
  // Check if photo has drone data
  const hasDroneData = (photo) => {
    return photo && photo.is_drone_photo === true;
  };
  
  // Handle upload submission
  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    
    try {
      // Create form data for upload
      const formData = new FormData();
      uploadFiles.forEach(file => {
        formData.append('photos', file);
      });
      
      // Upload photos
      const response = await api.post(
        `/inspections/${currentInspection.id}/photos`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      // For debugging
      console.log('Upload response:', response.data);
      
      let successfulUploads = false; // Flag to check if any uploads succeeded
      
      // Update photos list with newly uploaded photos
      if (response.data && response.data.photos && Array.isArray(response.data.photos)) {
        // Create enhanced photo objects with URL for frontend use
        const uploadedPhotos = response.data.photos.map(photo => {
          // The backend now provides a clean, web-relative path.
          // Construct the secure URL.
          const photoPath = photo.photo_path.replace(/\\/g, '/');
            
          return {
            ...photo,
            photo_path: photoPath,
            url: photoPath,
            has_annotation: photo.has_annotation || false,
            annotation_count: photo.annotation_count || 0
          };
        });
        setPhotos(prevPhotos => [...prevPhotos, ...uploadedPhotos]);
        
        if (response.data.photos.length > 0) {
          successfulUploads = true; // Mark that uploads were successful
        }
        
        // Check if any successful uploads with geo data
        const uploadCount = response.data.photos.length;
        const photosWithGeoData = response.data.photos.filter(
          photo => photo.latitude !== null && photo.longitude !== null
        ).length;
        
        // Set success message based on results
        if (uploadCount > 0) {
          if (photosWithGeoData > 0) {
            setSuccess(`Successfully uploaded ${uploadCount} photos (${photosWithGeoData} with geo-location data)`);
          } else {
            setSuccess(`Successfully uploaded ${uploadCount} photos (no geo-location data found)`);
          }
        } else {
          setSuccess('Photos processed, but none were successfully uploaded');
        }
      } else if (response.data && response.data.message) {
        // Handle case where API returns a success message but no photos
        setSuccess(response.data.message);
      }
      
      // Display any errors from processing
      if (response.data && response.data.errors && response.data.errors.length > 0) {
        const errorMessages = response.data.errors.map(err => 
          `${err.file}: ${err.error}`
        ).join('; ');
        setUploadError(`Some photos had issues: ${errorMessages}`);
      }
      
      // Clear upload files
      setUploadFiles([]);
      
      // Switch to gallery tab if we had successful uploads
      if (successfulUploads) {
        setTabValue(0);
        // Call the callback to notify parent that data changed after successful upload
        if (onDataMutated) {
          onDataMutated();
          console.log('Called onDataMutated after photo upload.');
        }
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
      
    } catch (err) {
      console.error('Error uploading photos:', err);
      let errorMessage = 'Failed to upload photos';
      
      if (err.response) {
        errorMessage += `: ${err.response.status} - ${err.response.data?.error || ''}`;
      }
      
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Handle cards per row change
  const handleCardsPerRowChange = (newValue) => {
    setCardsPerRow(newValue);
    setDensityMenuAnchor(null);
  };

  // Open density menu
  const handleDensityMenuOpen = (event) => {
    setDensityMenuAnchor(event.currentTarget);
  };

  // Close density menu
  const handleDensityMenuClose = () => {
    setDensityMenuAnchor(null);
  };

  // Calculate card width based on cards per row
  const getCardWidth = () => {
    // We need to account for gap (16px = 2 theme spacing units) between cards
    // Assuming the container is 100% of dialog width and we have gaps between cards
    const gapSize = 16; // 2 theme spacing units
    // For example: if cardsPerRow = 3, and we have 2 gaps, we calculate:
    // width = (100% - (gapSize * (cardsPerRow - 1))) / cardsPerRow
    
    // For simplicity, using fixed widths based on cards per row
    switch(cardsPerRow) {
      case 2: return 440; // Wide cards for 2 per row
      case 4: return 205; // Narrower cards for 4 per row (reduced from 220)
      case 3:
      default: return 300; // Medium cards for 3 per row
    }
  };

  // Function to format photo caption by removing the "Inspection image -" prefix
  const formatCaption = (caption) => {
    if (!caption) return '';
    // Remove "Inspection image -" prefix if present
    return caption.replace(/^Inspection image\s*-\s*/i, '');
  };

  // Handle photo deletion
  const handleDeletePhoto = async () => {
    if (!photoToDelete || !inspection) return;
    
    setDeleting(true);
    try {
      await api.delete(`/inspections/${inspection.id}/photos/${photoToDelete.id}`);
      
      // Remove photo from state
      setPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoToDelete.id));
      
      // Show success message
      setSuccess(`Photo "${formatCaption(photoToDelete.caption)}" successfully deleted`);
      
      // Call the callback to notify parent that data changed
      if (onDataMutated) {
        onDataMutated();
        console.log('Called onDataMutated after photo deletion.');
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error deleting photo:', err);
      let errorMessage = 'Failed to delete photo';
      
      if (err.response) {
        errorMessage += `: ${err.response.status} - ${err.response.data?.error || ''}`;
      }
      
      setError(errorMessage);
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setDeleting(false);
      setPhotoToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (photo, event) => {
    event.stopPropagation(); // Prevent card/row click
    setPhotoToDelete(photo);
    setDeleteConfirmOpen(true);
  };
  
  // Close delete confirmation dialog
  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setPhotoToDelete(null);
  };

  // Toggle selection mode for multi-photo deletion
  const toggleDeleteSelectionMode = () => {
    setSelectionModeForDelete(!selectionModeForDelete);
    if (selectionModeForDelete) {
      setSelectedPhotosForDelete({}); // Clear selections when exiting
    }
  };

  // Toggle individual photo selection for deletion
  const togglePhotoForDelete = (photoId) => {
    setSelectedPhotosForDelete(prev => ({
      ...prev,
      [photoId]: !prev[photoId]
    }));
  };

  // Select all photos for deletion
  const selectAllPhotosForDelete = () => {
    const allSelected = {};
    getFilteredPhotos().forEach(photo => {
      allSelected[photo.id] = true;
    });
    setSelectedPhotosForDelete(allSelected);
  };

  // Deselect all photos
  const deselectAllPhotosForDelete = () => {
    setSelectedPhotosForDelete({});
  };

  // Get count of selected photos
  const getSelectedDeleteCount = () => {
    return Object.values(selectedPhotosForDelete).filter(Boolean).length;
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    const selectedIds = Object.keys(selectedPhotosForDelete).filter(id => selectedPhotosForDelete[id]);
    if (selectedIds.length === 0 || !inspection) return;

    setBulkDeleting(true);
    setBulkDeleteProgress({ current: 0, total: selectedIds.length });

    let successCount = 0;
    for (const photoId of selectedIds) {
      try {
        await api.delete(`/inspections/${inspection.id}/photos/${photoId}`);
        successCount++;
        setBulkDeleteProgress(prev => ({ ...prev, current: prev.current + 1 }));
      } catch (err) {
        console.error(`Failed to delete photo ${photoId}:`, err);
      }
    }

    // Update photos state - filter out successfully deleted photos
    setPhotos(prev => prev.filter(p => !selectedPhotosForDelete[p.id]));

    // Show success message
    setSuccess(`Successfully deleted ${successCount} of ${selectedIds.length} photos`);

    // Call the callback to notify parent that data changed
    if (onDataMutated) {
      onDataMutated();
      console.log('Called onDataMutated after bulk photo deletion.');
    }

    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccess(false);
    }, 5000);

    // Reset state
    setBulkDeleting(false);
    setDeleteConfirmOpen(false);
    setSelectedPhotosForDelete({});
    setSelectionModeForDelete(false);
    setBulkDeleteProgress({ current: 0, total: 0 });
  };

  // Toggle defect summary expansion
  const toggleDefectSummary = () => {
    setDefectSummaryExpanded(!defectSummaryExpanded);
  };

  // Calculate total number of annotations
  const getTotalAnnotationCount = () => {
    return Object.values(defectCounts).reduce((sum, count) => sum + count, 0);
  };

  // Add this function inside the component
  const updateChipScrollArrows = () => {
    const container = document.getElementById('defect-chips-container');
    const leftButton = document.getElementById('defect-chips-left-scroll-button');
    const rightButton = document.getElementById('defect-chips-right-scroll-button');
    if (!container || !leftButton || !rightButton) return;
    // Left arrow
    leftButton.style.visibility = container.scrollLeft > 5 ? 'visible' : 'hidden';
    // Right arrow
    rightButton.style.visibility =
      container.scrollWidth > container.clientWidth &&
      container.scrollLeft + container.clientWidth < container.scrollWidth - 5
        ? 'visible'
        : 'hidden';
  };

  // useEffect to update arrows after chips render and on resize
  useEffect(() => {
    if (!defectTypesLoading && defectTypes.length > 0) {
      setTimeout(() => {
        updateChipScrollArrows();
        // Add ResizeObserver for robust detection
        const container = document.getElementById('defect-chips-container');
        if (container && window.ResizeObserver) {
          const observer = new ResizeObserver(() => updateChipScrollArrows());
          observer.observe(container);
          return () => observer.disconnect();
        }
      }, 150); // Slightly longer delay to ensure DOM is ready
    }
  }, [defectTypesLoading, defectTypes.length]);

  // Fix the fetchAnnotationCounts function to handle the photo_id parameter correctly
  const fetchAnnotationCounts = async (photoIds) => {
    if (!photoIds || photoIds.length === 0) return {};
    
    try {
      console.log('Fetching annotation counts for photos:', photoIds);
      
      // Make individual requests for each photo ID to avoid the array parameter issue
      const countPromises = photoIds.map(photoId => 
        api.get(`/bounding-boxes`, {
          params: { 
            photo_id: photoId 
          }
        })
      );
      
      const responses = await Promise.all(countPromises);
      
      // Process the responses and count bounding boxes for each photo
      const countsByPhotoId = {};
      responses.forEach((response, index) => {
        const photoId = photoIds[index];
        if (Array.isArray(response.data)) {
          countsByPhotoId[photoId] = response.data.length;
        } else {
          countsByPhotoId[photoId] = 0;
        }
      });
      
      console.log('Calculated annotation counts:', countsByPhotoId);
      return countsByPhotoId;
    } catch (err) {
      console.error('Error fetching annotation counts:', err);
      return {};
    }
  };

  // Add this useEffect to fetch annotation counts if they aren't already included
  useEffect(() => {
    if (!photos.length) return;
    
    const fetchMissingAnnotationCounts = async () => {
      // Check if any photos are missing annotation counts
      const photosNeedingCounts = photos.filter(photo => 
        photo.has_annotation === true && 
        (photo.annotation_count === undefined || photo.annotation_count === null)
      );
      
      if (photosNeedingCounts.length === 0) return;
      
      const photoIds = photosNeedingCounts.map(photo => photo.id);
      const annotationCounts = await fetchAnnotationCounts(photoIds);
      
      // Update photos with annotation counts
      if (Object.keys(annotationCounts).length > 0) {
        setPhotos(prevPhotos => 
          prevPhotos.map(photo => {
            if (annotationCounts[photo.id] !== undefined) {
              return {
                ...photo,
                annotation_count: annotationCounts[photo.id]
              };
            }
            return photo;
          })
        );
      }
    };
    
    fetchMissingAnnotationCounts();
  }, [photos]);

  // Add handler for defect type chip click
  const handleDefectTypeClick = (defectTypeId) => {
    // If already selected, clear the filter
    if (selectedDefectTypeId === defectTypeId) {
      setSelectedDefectTypeId(null);
    } else {
      setSelectedDefectTypeId(defectTypeId);
    }
  };

  // Add useEffect to handle filtering photos when a defect type is selected
  useEffect(() => {
    if (!selectedDefectTypeId) {
      // If no defect type is selected, show all photos
      setFilteredPhotos(photos);
      return;
    }

    const fetchPhotosWithDefectType = async () => {
      try {
        // Get all photo IDs
        const photoIds = photos.map(photo => photo.id).filter(id => id);
        if (photoIds.length === 0) return;
        
        // Create object to track which photos contain the selected defect type
        const photosWithDefect = {};
        
        // Process photos in batches
        const batchSize = 5;
        for (let i = 0; i < photoIds.length; i += batchSize) {
          const batch = photoIds.slice(i, i + batchSize);
          const batchPromises = batch.map(photoId => 
            api.get(`/bounding-boxes`, {
              params: {
                photo_id: photoId
              }
            })
          );
          
          const responses = await Promise.all(batchPromises);
          
          // Check each photo for bounding boxes with the selected defect type
          responses.forEach((response, index) => {
            const photoId = batch[index];
            if (Array.isArray(response.data)) {
              // Check if any bounding box has the selected defect type
              const hasSelectedDefect = response.data.some(box => 
                parseInt(box.defect_type_id, 10) === selectedDefectTypeId
              );
              photosWithDefect[photoId] = hasSelectedDefect;
            }
          });
        }
        
        // Filter photos to only those containing the selected defect type
        const filtered = photos.filter(photo => photosWithDefect[photo.id]);
        setFilteredPhotos(filtered);
        
      } catch (err) {
        console.error('Error filtering photos by defect type:', err);
        // On error, show all photos
        setFilteredPhotos(photos);
      }
    };
    
    fetchPhotosWithDefectType();
  }, [selectedDefectTypeId, photos]);

  // Add function to get filtered photos
  const getFilteredPhotos = () => {
    if (!selectedDefectTypeId) return photos;
    
    // Filter photos that have annotations with the selected defect type
    return photos.filter(photo => {
      // Check if this photo has any bounding boxes with the selected defect type
      return photo.defect_types && photo.defect_types.includes(selectedDefectTypeId);
    });
  };

  // Toggle report view and fetch defect data when entering report view
  const toggleReportView = () => {
    const newReportView = !reportView;
    setReportView(newReportView);
    
    // Turn off AI analysis view if activating report view
    if (newReportView) {
      setAiAnalysisView(false);
      
      // Fetch defect data when entering report view
      if (currentInspection?.id) {
        fetchDefectReport(currentInspection.id);
      }
    }
  };
  
  // Toggle AI analysis view
  const toggleAiAnalysisView = () => {
    const newAiAnalysisView = !aiAnalysisView;
    setAiAnalysisView(newAiAnalysisView);
    
    // Turn off report view if activating AI analysis view
    if (newAiAnalysisView) {
      setReportView(false);
      // Fetch AI data when entering AI view
      fetchAiData();
    } else {
      // Reset selection state when exiting AI view
      setSelectingImages(false);
      setSelectedImages({});
    }
  };

  // Toggle image selection mode for AI processing
  const toggleImageSelectionMode = () => {
    setSelectingImages(!selectingImages);
    // Reset selections when toggling off
    if (selectingImages) {
      setSelectedImages({});
    }
  };

  // Handle selecting/deselecting an image
  const toggleImageSelection = (photoId) => {
    setSelectedImages(prev => ({
      ...prev,
      [photoId]: !prev[photoId]
    }));
  };

  // Check if an image is selected
  const isImageSelected = (photoId) => {
    return Boolean(selectedImages[photoId]);
  };

  // Get count of selected images
  const getSelectedImagesCount = () => {
    return Object.values(selectedImages).filter(Boolean).length;
  };

  // Select all images
  const selectAllImages = () => {
    const allSelected = {};
    photos.forEach(photo => {
      allSelected[photo.id] = true;
    });
    setSelectedImages(allSelected);
  };

  // Deselect all images
  const deselectAllImages = () => {
    setSelectedImages({});
  };

  // Fetches and prepares all data required for the PDF report from a single inspection
  const fetchDefectReport = async (inspectionId, includeDrafts = false) => {
    setDefectReport({ loading: true, error: null, data: null });
    try {
      // 1. Fetch all necessary data in parallel
      const [defectsResponse, photosResponse] = await Promise.all([
        api.get(`/inspections/${inspectionId}/defects`),
        api.get(`/inspections/${inspectionId}/photos`),
      ]);

      const defects = defectsResponse.data || [];
      const allPhotos = photosResponse.data || [];
      
      // If including drafts, fetch draft bounding boxes that haven't been converted to defects yet
      let draftFindings = [];
      if (includeDrafts) {
        try {
          // Fetch bounding boxes for all photos in this inspection that are still in 'draft' status
          // Since there isn't a direct endpoint for "inspection draft findings", we iterate through photos
          // Optimization: Fetch bounding boxes for the inspection directly via a new query param if backend supported it,
          // but for now we'll rely on the existing bounding-boxes endpoint filtered by photo_ids
          
          // Collect all photo IDs
          const photoIds = allPhotos.map(p => p.id).filter(id => id);
          
          // We can't easily fetch all bounding boxes for an inspection in one go with current API
          // So we will fetch bounding boxes for photos that have annotations
          const photosWithAnnotations = allPhotos.filter(p => p.has_annotation);
          
          const draftPromises = photosWithAnnotations.map(photo =>
            api.get(`/bounding-boxes`, {
              params: {
                photo_id: photo.id,
                status: 'draft'
              }
            })
          );
          
          const draftResponses = await Promise.all(draftPromises);
          
          // Process responses to extract draft bounding boxes
          draftResponses.forEach((response, index) => {
            if (Array.isArray(response.data) && response.data.length > 0) {
              const photo = photosWithAnnotations[index];
              const boxes = response.data.map(box => ({
                // Format draft finding to look like a defect for the report
                id: `draft-${box.id}`,
                defect_type_id: box.defect_type_id,
                defect_type_name: box.defect_type_name || 'Unknown Defect',
                description: box.notes || 'Draft finding from AI/Manual annotation',
                status: 'draft',
                severity_level_name: box.severity_level_name,
                severity_level_id: box.severity_level_id,
                created_at: box.created_at,
                // Important: link to photo
                photo_path: photo.photo_path,
                bounding_box: box, // Attach the box directly
                is_draft: true
              }));
              draftFindings = [...draftFindings, ...boxes];
            }
          });
          
          console.log(`Fetched ${draftFindings.length} draft findings for report preview`);
          
        } catch (draftErr) {
          console.error('Error fetching draft findings:', draftErr);
          // Don't fail the whole report if drafts fail
        }
      }

      // Combine confirmed defects and draft findings (if enabled)
      const allFindings = [...defects, ...draftFindings];

      // Use photo_path as the key for the map
      const photoMap = new Map(allPhotos.map(p => [p.photo_path, p]));

      // 2. Fetch bounding boxes for each confirmed defect (drafts already have them)
      const defectsWithBoundingBoxes = await Promise.all(
        allFindings.map(async (item) => {
          if (item.is_draft) return item; // Drafts already have bbox attached
          
          return api.get(`/defects/${item.id}/bounding-box`)
            .then(bboxResponse => ({ ...item, bounding_box: bboxResponse.data }))
            .catch(err => {
              console.warn(`Failed to fetch bounding box for defect ${item.id}:`, err.message);
              return { ...item, bounding_box: null };
            });
        })
      );

      // 3. Enrich defects with photo paths and collect unique photo paths
      const photoPathsWithDefects = new Set();
      const enrichedDefects = defectsWithBoundingBoxes.map(defect => {
        if (defect.photo_path && photoMap.has(defect.photo_path)) {
          const photo = photoMap.get(defect.photo_path);
          photoPathsWithDefects.add(defect.photo_path);
          // Add the crucial missing path and dimensions for the report
          return {
            ...defect,
            original_image_path: photo.photo_path,
            image_width: photo.image_width,
            image_height: photo.image_height,
          };
        }
        return defect;
      });

      // 4. Filter all photos to get only those that have defects (or drafts)
      const photosWithDefects = allPhotos.filter(photo => photoPathsWithDefects.has(photo.photo_path));

      // 5. Finalize the report data structure
      const finalReportData = {
        total: enrichedDefects.length,
        defects: enrichedDefects,
        defectTypes: calculateDefectTypesFromDefects(enrichedDefects),
        photosWithDefects: photosWithDefects,
        photosWithDefectsCount: photosWithDefects.length,
        isDraftPreview: includeDrafts
      };

      // 6. Update the state
      setDefectReport({
        loading: false,
        error: null,
        data: finalReportData,
      });
      // Reset the user's exclusion selection so the new findings list starts fully selected
      setExcludedDefectIds(new Set());

    } catch (err) {
      console.error('Error fetching defect report:', err);
      setDefectReport({
        loading: false,
        error: 'Failed to load defect report. Please try again.',
        data: null,
      });
    }
  };

  // Helper function to calculate defect types if not provided directly in API
  const calculateDefectTypesFromDefects = (data) => {
    // If data is an array, it's the defects array
    const defects = Array.isArray(data) ? data : (data.defects || []);
    
    // Group defects by defect type
    const defectTypesMap = {};
    defects.forEach(defect => {
      if (defect.defect_type_id && defect.defect_type_name) {
        if (!defectTypesMap[defect.defect_type_id]) {
          defectTypesMap[defect.defect_type_id] = {
            defect_type_id: defect.defect_type_id,
            defect_type_name: defect.defect_type_name,
            count: 0
          };
        }
        defectTypesMap[defect.defect_type_id].count++;
      }
    });
    
    return Object.values(defectTypesMap);
  };

  // ----- Findings Review (defect selection for PDF) helpers -----
  // Build an ordering map keyed by photo_path so defects from the same photo render consecutively,
  // following the order in which photosWithDefects was returned by the API.
  const sortedDefectsForReview = useMemo(() => {
    const defects = defectReport.data?.defects;
    if (!Array.isArray(defects) || defects.length === 0) return [];

    const photoOrder = new Map();
    (defectReport.data?.photosWithDefects || []).forEach((p, idx) => {
      if (p && p.photo_path) photoOrder.set(p.photo_path, idx);
    });

    return [...defects]
      .filter(d => d && d.photo_path)
      .sort((a, b) => {
        const oA = photoOrder.has(a.photo_path) ? photoOrder.get(a.photo_path) : Number.MAX_SAFE_INTEGER;
        const oB = photoOrder.has(b.photo_path) ? photoOrder.get(b.photo_path) : Number.MAX_SAFE_INTEGER;
        if (oA !== oB) return oA - oB;
        const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tA - tB;
      });
  }, [defectReport.data]);

  // Lookup map: photo_path -> photo (for caption / index in the review list)
  const photoByPath = useMemo(() => {
    const map = new Map();
    (defectReport.data?.photosWithDefects || []).forEach((p, idx) => {
      if (p && p.photo_path) map.set(p.photo_path, { photo: p, index: idx + 1 });
    });
    return map;
  }, [defectReport.data]);

  // Counts derived from current selection
  const findingsTotal = sortedDefectsForReview.length;
  const findingsExcluded = sortedDefectsForReview.reduce(
    (acc, d) => acc + (excludedDefectIds.has(String(d.id)) ? 1 : 0),
    0
  );
  const findingsSelected = findingsTotal - findingsExcluded;

  const toggleDefectExclusion = (defectId) => {
    const key = String(defectId);
    setExcludedDefectIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAllDefects = () => {
    setExcludedDefectIds(new Set());
  };

  const handleDeselectAllDefects = () => {
    setExcludedDefectIds(new Set(sortedDefectsForReview.map(d => String(d.id))));
  };
  // ----- end Findings Review helpers -----

  // Add a useEffect to reset the report view state when the modal is opened
  useEffect(() => {
    // Reset to gallery view whenever the modal opens
    if (open) {
      setReportView(false);
      setAiAnalysisView(false);
      setReportPreviewMode(false);
      setDraftPreviewMode(false);
      setReportDate(new Date()); // Reset report date to today
      setCurrentAssetType(null); // Reset asset type
      
      // Also reset the defect report data when opening
      setDefectReport({
        loading: false,
        error: null,
        data: null
      });
      // Clear any stale defect exclusion selection from a previous open
      setExcludedDefectIds(new Set());
    }
  }, [open]);

  // Open PDF preview in a new tab
  const handlePreviewReport = () => {
    if (!currentInspection?.id) return;

    // Build the URL with query params
    const url = new URL(`/report-preview/${currentInspection.id}`, window.location.origin);
    url.searchParams.set('date', reportDate.toISOString());

    // If the user has unticked any defects in the Findings Review list, persist the exclusion
    // list to localStorage under a unique nonce so the new-tab preview page can read & apply it.
    // Only Preview PDF (validated findings) honors the selection - Preview Draft Finding does not.
    if (excludedDefectIds && excludedDefectIds.size > 0) {
      try {
        const nonce = `${currentInspection.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const payload = {
          inspectionId: currentInspection.id,
          excluded: Array.from(excludedDefectIds).map(String),
          createdAt: Date.now(),
        };
        localStorage.setItem(`pdf-report-exclude-${nonce}`, JSON.stringify(payload));
        url.searchParams.set('selectionKey', nonce);
      } catch (e) {
        console.warn('[InspectionDetailsModal] Failed to persist defect exclusion list:', e);
      }
    }

    // Open in new tab
    window.open(url.toString(), '_blank');
  };

  // Construct report summary object for single asset inspection
  const getReportSummary = () => {
    if (!currentInspection) return null;
    
    // Ensure we have a valid date object for the inspection date
    // Prioritize inspection_date from DB, fallback to date, then current date
    const inspectionDateStr = currentInspection.inspection_date || currentInspection.date;
    const inspectionDate = inspectionDateStr ? new Date(inspectionDateStr) : new Date();
    
    // Use assetDetails if available to get network and section info
    // Note: This relies on assetDetails being set during the defect fetch process or we might need to fetch it separately
    
    return {
      network: { name: assetDetails?.network_name || 'N/A' },
      // Replace Sections with Asset ID for single inspection reports
      customField: {
        label: 'Asset ID',
        value: assetDetails?.code || 'N/A'
      },
      // Add Asset Rating (from asset's rating_level_id) - displayed as Rx (Hazard/Risk Ranking)
      assetRating: assetDetails?.rating_name || 'Not Rated',
      // Add Inspection Type to the summary
      inspectionType: currentInspection.inspection_type_name || 'Standard Inspection',
      // Add Inspection Cycle
      inspectionCycle: currentInspection.cycle || null,
      // Inspection's condition rating (from inspection's condition_rating_level_id) - displayed as Cx
      inspectionConditionRating: currentInspection.condition_rating_name || 'Not Rated',
      startDate: inspectionDate,
      endDate: inspectionDate,
      selectAllDefects: true, // For single inspection, we typically show all defects
      selectedDefectTypes: defectTypes, // Pass all available defect types for this asset
      totalDefectTypes: defectTypes.length,
      generatedBy: user?.fullName || user?.full_name || user?.username || 'System User'
    };
  };
  
  // Open PDF preview with draft findings in a new tab
  const handlePreviewDraftReport = () => {
    if (!currentInspection?.id) return;

    // Build the URL with query params including drafts=true
    const url = new URL(`/report-preview/${currentInspection.id}`, window.location.origin);
    url.searchParams.set('date', reportDate.toISOString());
    url.searchParams.set('drafts', 'true');

    // Open in new tab
    window.open(url.toString(), '_blank');
  };

  // Add a function to handle exiting preview mode
  const handleBackToReport = () => {
    setReportPreviewMode(false);
    setDraftPreviewMode(false);
    // Re-fetch original report data (without drafts) to restore view
    if (currentInspection?.id) {
      fetchDefectReport(currentInspection.id, false);
    }
  };

  // Add useEffect to reset selection state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectingImages(false);
      setSelectedImages({});
    }
  }, [open]);

  // Function to save AI results - wrapped in useCallback to prevent stale closures
  const handleSaveResults = useCallback(async (batchResult, batchNumber) => {
    if (!currentInspection || !currentInspection.id || !batchResult) {
      console.error('[AI Analysis] Cannot save results: inspection ID or result data is missing.', {
        hasInspection: !!currentInspection,
        inspectionId: currentInspection?.id,
        hasResult: !!batchResult
      });
      return false;
    }
    try {
      console.log(`[AI Analysis] Saving batch ${batchNumber} results with ${batchResult.results?.length || 0} image results`);
      
      await api.post(`/inspections/${currentInspection.id}/save-ai-results`, { results: batchResult.results });
      
      console.log(`[AI Analysis] ✓ Batch ${batchNumber} results saved successfully to database`);
      
      // Accumulate results
      setAiJob(prev => ({
        ...prev,
        allResults: [...prev.allResults, ...(batchResult.results || [])]
      }));
      
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to save AI results.';
      console.error(`[AI Analysis] ✗ Failed to save batch ${batchNumber} results:`, errorMessage, err);
      setError(`Failed to save batch ${batchNumber} results: ${errorMessage}`);
      return false;
    }
  }, [currentInspection]);

  // Sequential batch polling - processes one batch at a time
  useEffect(() => {
    if (aiJob.status !== 'PENDING' && aiJob.status !== 'PROGRESS') {
      return;
    }
    
    if (!aiJob.jobs || aiJob.jobs.length === 0) {
      return;
    }

    const currentBatch = aiJob.jobs[aiJob.currentBatchIndex];
    if (!currentBatch || currentBatch.status === 'SUCCESS' || currentBatch.status === 'FAILED') {
      return;
    }

    // If current batch is still being submitted (job_id = 'pending'), poll backend to check if it's been created
    if (currentBatch.job_id === 'pending' || currentBatch.status === 'SUBMITTING') {
      console.log(`[AI Analysis] Batch ${currentBatch.batch_number} is pending submission, checking backend...`);
      
      const checkIntervalId = setInterval(async () => {
        try {
          const jobsResponse = await api.get(`/inspections/${currentInspection.id}/ai-jobs`);
          const backendJobs = jobsResponse.data.jobs;
          
          // Find the job for the current batch
          const realJob = backendJobs.find(j => j.batch_number === currentBatch.batch_number);
          
          if (realJob && realJob.job_id !== 'pending') {
            console.log(`[AI Analysis] Batch ${currentBatch.batch_number} has been submitted! job_id: ${realJob.job_id}`);
            clearInterval(checkIntervalId);
            
            // Update the job in state
            setAiJob(prev => {
              const updatedJobs = [...prev.jobs];
              updatedJobs[prev.currentBatchIndex] = {
                ...updatedJobs[prev.currentBatchIndex],
                job_id: realJob.job_id,
                status: 'PENDING'
              };
              return { ...prev, jobs: updatedJobs };
            });
          }
        } catch (error) {
          console.error(`[AI Analysis] Error checking job status:`, error);
        }
      }, 3000); // Check every 3 seconds
      
      return () => {
        clearInterval(checkIntervalId);
        console.log(`[AI Analysis] Stopped checking for batch ${currentBatch.batch_number}`);
      };
    }

    console.log(`[AI Analysis] Starting to poll batch ${currentBatch.batch_number} (job_id: ${currentBatch.job_id})`);

    const visionApiBaseUrl = 'https://vision-api.inxpect.cloud';
    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get(`${visionApiBaseUrl}/jobs/${currentBatch.job_id}/status`);
        const { status, progress, result } = response.data;

        console.log(`[AI Analysis] Batch ${currentBatch.batch_number} status: ${status}`);

        if (status === 'SUCCESS') {
          // Clear interval immediately
          clearInterval(intervalId);
          
          console.log(`[AI Analysis] Batch ${currentBatch.batch_number} completed, saving results...`);
          
          // Save this batch's results
          const saved = await handleSaveResults(result, currentBatch.batch_number);
          
          if (!saved) {
            console.error(`[AI Analysis] Failed to save batch ${currentBatch.batch_number}, but continuing...`);
          }
          
          // Update batch status
          setAiJob(prev => {
            const updatedJobs = [...prev.jobs];
            updatedJobs[prev.currentBatchIndex] = {
              ...updatedJobs[prev.currentBatchIndex],
              status: 'SUCCESS',
              progress: null,
              results: result
            };

            // Check if there are more batches
            const nextBatchIndex = prev.currentBatchIndex + 1;
            const hasMoreBatches = nextBatchIndex < prev.jobs.length;

            if (hasMoreBatches) {
              // Move to next batch
              console.log(`[AI Analysis] Moving to batch ${nextBatchIndex + 1}/${prev.jobs.length}`);
              return {
                ...prev,
                jobs: updatedJobs,
                currentBatchIndex: nextBatchIndex,
                status: 'PENDING'
              };
            } else {
              // All batches completed
              console.log(`[AI Analysis] All ${prev.jobs.length} batches completed successfully`);
              
              // Update inspection status to 'in_progress' after all batches complete
              if (currentInspection?.id) {
                api.put(`/inspections/${currentInspection.id}`, { status: 'in_progress' })
                  .then(() => {
                    setSuccess('AI analysis completed and inspection status updated to In Progress.');
                  })
                  .catch((statusUpdateError) => {
                    console.error('Failed to update inspection status:', statusUpdateError);
                    setSuccess('AI analysis completed, but failed to update inspection status.');
                  });
              }

              if (onDataMutated) {
                onDataMutated();
              }

              // Reset to IDLE after 10 seconds
              setTimeout(() => {
                setAiJob({
                  jobs: [],
                  currentBatchIndex: 0,
                  status: 'IDLE',
                  overallProgress: null,
                  error: null,
                  failedBatches: [],
                  allResults: []
                });
              }, 10000);

              return {
                ...prev,
                jobs: updatedJobs,
                status: 'SUCCESS',
                overallProgress: null
              };
            }
          });
          
        } else if (status === 'FAILURE') {
          clearInterval(intervalId);
          
          const errorMessage = result || 'AI processing job failed.';
          console.error(`[AI Analysis] Batch ${currentBatch.batch_number} failed:`, errorMessage);
          
          setAiJob(prev => {
            const updatedJobs = [...prev.jobs];
            updatedJobs[prev.currentBatchIndex] = {
              ...updatedJobs[prev.currentBatchIndex],
              status: 'FAILED',
              error: errorMessage
            };

            const failedBatches = [...prev.failedBatches, {
              batch_number: currentBatch.batch_number,
              error: errorMessage
            }];

            // Check if there are more batches
            const nextBatchIndex = prev.currentBatchIndex + 1;
            const hasMoreBatches = nextBatchIndex < prev.jobs.length;

            if (hasMoreBatches) {
              // Continue with next batch despite failure
              console.log(`[AI Analysis] Continuing to next batch despite batch ${currentBatch.batch_number} failure`);
              return {
                ...prev,
                jobs: updatedJobs,
                currentBatchIndex: nextBatchIndex,
                status: 'PENDING',
                failedBatches
              };
            } else {
              // All batches processed (some failed)
              setError(`AI analysis completed with ${failedBatches.length} failed batch(es)`);
              
              // Still try to update inspection status if we have some results
              if (prev.allResults.length > 0 && currentInspection?.id) {
                api.put(`/inspections/${currentInspection.id}`, { status: 'in_progress' })
                  .catch((err) => console.error('Failed to update inspection status:', err));
                
                if (onDataMutated) {
                  onDataMutated();
                }
              }

              return {
                ...prev,
                jobs: updatedJobs,
                status: 'PARTIAL_SUCCESS',
                failedBatches
              };
            }
          });
          
        } else {
          // Still in progress
          setAiJob(prev => {
            const updatedJobs = [...prev.jobs];
            updatedJobs[prev.currentBatchIndex] = {
              ...updatedJobs[prev.currentBatchIndex],
              status: 'PROGRESS',
              progress
            };
            
            return {
              ...prev,
              jobs: updatedJobs,
              status: 'PROGRESS'
            };
          });
        }
      } catch (err) {
        console.error(`[AI Analysis] Error polling batch ${currentBatch.batch_number}:`, err);
        clearInterval(intervalId);
        
        const errorMessage = err.response?.data?.error || 'Failed to get job status.';
        
        setAiJob(prev => ({
          ...prev,
          status: 'FAILED',
          error: errorMessage
        }));
        setError(errorMessage);
      }
    }, 5000); // Poll every 5 seconds

    return () => {
      console.log(`[AI Analysis] Cleaning up polling for batch ${currentBatch.batch_number}`);
      clearInterval(intervalId);
    };
  }, [aiJob.status, aiJob.currentBatchIndex, aiJob.jobs, handleSaveResults, currentInspection, onDataMutated]);

  // Function to check for existing AI annotations
  const checkForExistingAiAnnotations = () => {
    const photosWithAi = photos.filter(photo => 
      photo.has_annotation && photo.annotation_count > 0
    );
    return photosWithAi;
  };

  // Function to generate dynamic report title based on asset type
  const generateReportTitle = () => {
    if (!currentAssetType) {
      // Add Draft indicator to title if needed
      const title = draftPreviewMode ? "Road Inspection Report (Draft Findings)" : "Road Inspection Report";
      return title; // Default fallback
    }
    
    const draftSuffix = draftPreviewMode ? " (Draft Findings)" : "";
    
    // Convert asset type to proper title case
    const assetTypeName = currentAssetType.toLowerCase();
    
    switch (assetTypeName) {
      case 'bridge':
        return `Bridge Inspection Report${draftSuffix}`;
      case 'drainage':
        return `Drainage Inspection Report${draftSuffix}`;
      case 'road':
        return `Road Inspection Report${draftSuffix}`;
      case 'tunnel':
        return `Tunnel Inspection Report${draftSuffix}`;
      case 'culvert':
        return `Culvert Inspection Report${draftSuffix}`;
      case 'retaining wall':
        return `Retaining Wall Inspection Report${draftSuffix}`;
      case 'slope':
        return `Slope Inspection Report${draftSuffix}`;
      default:
        // For any other asset types, capitalize first letter and add "Inspection Report"
        const capitalizedType = currentAssetType.charAt(0).toUpperCase() + currentAssetType.slice(1).toLowerCase();
        return `${capitalizedType} Inspection Report${draftSuffix}`;
    }
  };

  // Function to handle AI analysis with warning check
  const handleRunAnalysis = async () => {
    if (!currentInspection || !currentInspection.id) {
      setError('Cannot start analysis: inspection ID is missing.');
      return;
    }

    // Check for existing AI annotations
    const aiAnnotatedPhotos = checkForExistingAiAnnotations();
    
    if (aiAnnotatedPhotos.length > 0) {
      setImagesWithAiAnnotations(aiAnnotatedPhotos);
      setAiWarningDialogOpen(true);
      return; // Stop here and show warning dialog
    }

    // Proceed with analysis if no existing annotations
    proceedWithAiAnalysis();
  };

  // Function to actually start the AI analysis
  const proceedWithAiAnalysis = async () => {
    setAiJob({ 
      jobs: [],
      currentBatchIndex: 0,
      status: 'SUBMITTING',
      overallProgress: null,
      error: null,
      failedBatches: [],
      allResults: []
    });

    try {
      // Prepare request body with selected model if available
      const requestBody = {};
      if (selectedModel && selectedModel.model_id) {
        requestBody.model_id = selectedModel.model_id;
      }

      const response = await api.post(`/inspections/${currentInspection.id}/start-ai-analysis`, requestBody);
      
      // Check if it's the new batch response format
      if (response.data.jobs && Array.isArray(response.data.jobs)) {
        // New batch format
        const batchJobs = response.data.jobs.map(job => ({
          ...job,
          status: job.job_id === 'pending' ? 'SUBMITTING' : 'PENDING',
          progress: null,
          results: null
        }));
        
        setAiJob({
          jobs: batchJobs,
          currentBatchIndex: 0,
          status: 'PENDING',
          overallProgress: null,
          error: null,
          failedBatches: response.data.failed_batches || [],
          allResults: [],
          totalBatches: response.data.total_batches
        });

        if (response.data.failed_batches && response.data.failed_batches.length > 0) {
          setError(`Warning: ${response.data.failed_batches.length} batch(es) failed to submit`);
        }
      } else if (response.data.job_id) {
        // Legacy single job format (fallback)
        setAiJob({
          jobs: [{
            job_id: response.data.job_id,
            batch_number: 1,
            total_batches: 1,
            batch_size: photos.length,
            status: 'PENDING',
            progress: null,
            results: null
          }],
          currentBatchIndex: 0,
          status: 'PENDING',
          overallProgress: null,
          error: null,
          failedBatches: [],
          allResults: []
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to start AI analysis job.';
      setAiJob({
        jobs: [],
        currentBatchIndex: 0,
        status: 'FAILED',
        overallProgress: null,
        error: errorMessage,
        failedBatches: [],
        allResults: []
      });
      setError(errorMessage);
    }
  };

  // Function to close warning dialog and proceed
  const handleProceedWithAnalysis = () => {
    setAiWarningDialogOpen(false);
    proceedWithAiAnalysis();
  };

  // Function to cancel AI analysis
  const handleCancelAnalysis = () => {
    setAiWarningDialogOpen(false);
    setImagesWithAiAnnotations([]);
  };

  const simulateAnalysis = () => {
    setIsAnalyzing(true);
    setAnalyzedImages({});
    
    // Reset analyzed state
    const selectedIds = Object.keys(selectedImages).filter(id => selectedImages[id]);
    
    // Simulate analysis completion for each image over time
    selectedIds.forEach((photoId, index) => {
      // Stagger completion times to make it look more realistic
      const completionTime = 3000 + (Math.random() * 7000); // Between 3 and 10 seconds
      
      setTimeout(() => {
        setAnalyzedImages(prev => ({
          ...prev,
          [photoId]: true
        }));
        
        // If this is the last image, finish the analysis after a small delay
        if (index === selectedIds.length - 1) {
          setTimeout(() => {
            setIsAnalyzing(false);
            // Show success message
            setSuccess(`Successfully analyzed ${selectedIds.length} images`);
            
            // Clear success message after 5 seconds
            setTimeout(() => {
              setSuccess(false);
            }, 5000);
            
            // Exit selection mode after analysis
            setTimeout(() => {
              toggleImageSelectionMode();
            }, 2000);
          }, 1000);
        }
      }, completionTime);
    });
  };

  // Handle rating selection and update inspection
  const handleRatingChange = async (ratingLevelId) => {
    if (ratingUpdateLoading || !currentInspection?.id) return;

    try {
      setRatingUpdateLoading(true);
      
      // Optimistically update local state first
      setSelectedRating(ratingLevelId);
      
      const response = await api.put(`/inspections/${currentInspection.id}`, {
        condition_rating_level_id: ratingLevelId,
        status: 'rated'
      });

      if (response.data) {
        
        // Update the current inspection object locally to ensure persistence
        setCurrentInspection(prev => ({
          ...prev,
          condition_rating_level_id: parseInt(ratingLevelId),
          status: 'rated'
        }));
        
        console.log('[InspectionDetailsModal] Updated local currentInspection object');
        
        // Show success message
        const selectedRatingLevel = availableRatings.find(r => r.id === parseInt(ratingLevelId));
        const ratingName = selectedRatingLevel ? selectedRatingLevel.name : 'Selected rating';
        setSuccess(`Inspection rated as "${ratingName}" and marked as complete`);
        
        // Clear success message after 4 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 4000);

        // Notify parent component of data mutation
        if (onDataMutated) {
          console.log('[InspectionDetailsModal] Calling onDataMutated callback');
          onDataMutated();
        }
      }
    } catch (err) {
      console.error('[InspectionDetailsModal] Error updating inspection rating:', err);
      setError('Failed to update inspection rating');
      
      // Revert selection on error
      const originalRating = currentInspection?.condition_rating_level_id ? String(currentInspection.condition_rating_level_id) : '';
      setSelectedRating(originalRating);
      console.log('[InspectionDetailsModal] Reverted rating to:', originalRating);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setRatingUpdateLoading(false);
    }
  };

  // Wrapper component that handles both modal and fullscreen modes
  const ContentWrapper = fullScreenMode ? Box : Dialog;
  const contentWrapperProps = fullScreenMode
    ? {
        sx: {
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fa',
          overflow: 'hidden',
        }
      }
    : {
        open: open,
        onClose: onClose,
        maxWidth: "lg",
        fullWidth: true,
        PaperProps: {
          sx: {
            borderRadius: 2,
            minHeight: '80vh',
            maxHeight: '90vh',
            width: '100%',
            maxWidth: '1200px',
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fa',
            backgroundImage: 'none',
            boxShadow: theme.shadows[8]
          }
        }
      };

  // Don't render anything if not open (for non-fullscreen mode, Dialog handles this)
  if (!fullScreenMode && !open) return null;

  return (
    <ContentWrapper {...contentWrapperProps}>
      <DialogTitle sx={{ 
        pb: 0.5, 
        pt: 1.5,
        px: 2.5,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : '#fff'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {reportView ? generateReportTitle() : (
              aiAnalysisView ? 'AI Defect Analysis' : (currentInspection?.title || 'Inspection Details')
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {currentInspection?.date ? new Date(currentInspection.date).toLocaleString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) : ''}
          </Typography>
        </Box>
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* AI Button - First */}
          <Button
            variant={aiAnalysisView ? "contained" : "outlined"}
            color="primary"
            size="small"
            startIcon={<AutoAwesomeIcon />}
            onClick={toggleAiAnalysisView}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderRadius: 1.5,
              height: 32,
              bgcolor: aiAnalysisView ? (
                theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.8) : theme.palette.primary.main
              ) : 'transparent',
              '&:hover': {
                bgcolor: aiAnalysisView ? (
                  theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.dark
                ) : alpha(theme.palette.primary.main, 0.08),
              }
            }}
          >
            {aiAnalysisView ? "Back to Gallery" : "AI Analysis"}
          </Button>

          {/* Rating Selector - Second */}
          {availableRatings.length > 0 && !reportView && !aiAnalysisView && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Rating Selector */}
              <FormControl 
                size="small" 
                variant="outlined"
                disabled={ratingUpdateLoading}
                sx={{ 
                  minWidth: selectedRating ? 120 : 140,
                  height: 32,
                  '& .MuiOutlinedInput-root': {
                    height: 32,
                    fontSize: '0.8rem',
                    borderRadius: 1.5,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    },
                    '&.Mui-focused': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: 'transparent',
                    }
                  },
                }}
              >
                <Select
                  labelId="rating-select-label"
                  value={selectedRating}
                  onChange={(e) => handleRatingChange(e.target.value)}
                  displayEmpty
                  renderValue={(selectedValue) => {
                    const rating = availableRatings.find(r => String(r.id) === String(selectedValue));
                    const labelText = selectedValue && rating ? `Rated: ${rating.name}` : 'Rate Inspection';
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StarIcon 
                          sx={{ 
                            fontSize: '1rem',
                            color: selectedValue ? theme.palette.warning.main : theme.palette.text.secondary
                          }} 
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {labelText}
                        </Typography>
                      </Box>
                    );
                  }}
                  sx={{
                    height: 32,
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      paddingTop: '6px',
                      paddingBottom: '6px'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select Rating</em>
                  </MenuItem>
                  {availableRatings.map((rating) => (
                      <MenuItem key={rating.id} value={rating.id}>
                        <Tooltip title={rating.description || ''} placement="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: rating.color_code || '#ccc',
                                border: '1px solid',
                                borderColor: alpha(theme.palette.text.primary, 0.2),
                                flexShrink: 0
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {rating.name} ({rating.value})
                            </Typography>
                          </Box>
                        </Tooltip>
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Generate Report Button - Third */}
          <Button
            variant={reportView ? "contained" : "outlined"}
            color="primary"
            size="small"
            startIcon={<DescriptionIcon />}
            onClick={toggleReportView}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderRadius: 1.5,
              height: 32
            }}
          >
            {reportView ? "Back to Gallery" : "Generate Report"}
          </Button>

          {/* Close Button */}
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      {/* Conditionally render content based on view state */}
      {!reportView && !aiAnalysisView ? (
        <>
          {/* Defect Type Summary Section */}
          <Box sx={{ 
            px: 2.5, 
            py: 0.75,
            display: 'flex', 
            flexDirection: 'row',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
            minHeight: '40px',
            overflow: 'hidden'
          }}>
            {/* ... existing defect summary content ... */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              minWidth: 'auto', 
              flexShrink: 0,
              pr: 1
            }}>
              <AssessmentIcon sx={{ mr: 0.75, color: theme.palette.info.main, fontSize: '1rem' }} />
              <Typography variant="subtitle2" fontWeight={600} fontSize="0.8rem" noWrap>
                Defect Classification Summary
              </Typography>
              {getTotalAnnotationCount() > 0 && (
                <Badge 
                  badgeContent={getTotalAnnotationCount()} 
                  color="secondary"
                  sx={{ ml: 1 }}
                  size="small"
                >
                  <BugReportIcon sx={{ color: theme.palette.secondary.main, fontSize: '0.9rem' }} />
                </Badge>
              )}
            </Box>
              
            {/* Display defect type chips in line with the title */}
            {!defectTypesLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                flexGrow: 1,
                position: 'relative',
                ml: 1,
                mr: 1,
              }}>
                {/* Left scroll arrow - conditionally rendered only when needed */}
                <IconButton 
                  id="defect-chips-left-scroll-button"
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    left: -5, 
                    zIndex: 2, 
                    p: 0.3,
                    visibility: 'hidden', // Start hidden, will be shown via JS when scrollable
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    display: 'flex',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    boxShadow: theme.shadows[2]
                  }}
                  onClick={() => {
                    const container = document.getElementById('defect-chips-container');
                    if (container) {
                      container.scrollBy({ left: -100, behavior: 'smooth' });
                      // After scrolling, check if we should hide this button
                      setTimeout(() => {
                        if (container.scrollLeft <= 5) { // Small threshold
                          document.getElementById('defect-chips-left-scroll-button').style.visibility = 'hidden';
                        }
                        // Always ensure right button is visible after scrolling left
                        document.getElementById('defect-chips-right-scroll-button').style.visibility = 'visible';
                      }, 350); // After scroll animation
                    }
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: '0.7rem' }} />
                </IconButton>
                
                {/* Chips container with hidden scrollbar */}
                <Box 
                  id="defect-chips-container"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.75,
                    px: 2, // Reduced side padding to avoid overlap with arrows
                    overflow: 'auto',
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                    scrollbarWidth: 'none', // Hide scrollbar for Firefox
                    msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
                    '&::-webkit-scrollbar': { // Hide scrollbar for Chrome/Safari
                      display: 'none'
                    }
                  }}
                  onScroll={(e) => {
                    updateChipScrollArrows();
                  }}
                >
                  {defectTypes.map((type) => {
                    // Convert type.id to number to ensure proper comparison with countsByType keys
                    const typeId = typeof type.id === 'string' ? parseInt(type.id, 10) : type.id;
                    const count = defectCounts[typeId] || 0;
                    const isSelected = selectedDefectTypeId === typeId;
                    
                    return (
                      <Chip
                        key={type.id}
                        size="small"
                        label={`${type.name}: ${count}`}
                        color={isSelected ? "primary" : (count > 0 ? "secondary" : "default")}
                        variant={isSelected ? "filled" : "outlined"}
                        onClick={() => handleDefectTypeClick(typeId)}
                        sx={{ 
                          height: 22,
                          flexShrink: 0,
                          cursor: 'pointer',
                          '& .MuiChip-label': { 
                            px: 1,
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }
                        }}
                      />
                    );
                  })}
                  
                  {defectTypes.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No defect types defined for this asset
                    </Typography>
                  )}
                </Box>
                
                {/* Right scroll arrow - conditionally rendered only when needed */}
                <IconButton 
                  id="defect-chips-right-scroll-button"
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    right: -5, 
                    zIndex: 2, 
                    p: 0.3,
                    visibility: 'visible', // Start visible if scrollable
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    display: 'flex',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    boxShadow: theme.shadows[2]
                  }}
                  onClick={() => {
                    const container = document.getElementById('defect-chips-container');
                    if (container) {
                      container.scrollBy({ left: 100, behavior: 'smooth' });
                      // After scrolling, check if we should hide this button
                      setTimeout(() => {
                        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 5) {
                          document.getElementById('defect-chips-right-scroll-button').style.visibility = 'hidden';
                        }
                        // Always ensure left button is visible after scrolling right (if we're not at the start)
                        if (container.scrollLeft > 5) {
                          document.getElementById('defect-chips-left-scroll-button').style.visibility = 'visible';
                        }
                      }, 350); // After scroll animation
                    }
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: '0.7rem' }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={14} thickness={4} />
                <Typography variant="caption" sx={{ ml: 1 }}>Loading defect types...</Typography>
              </Box>
            )}
          </Box>
          
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
              '& .MuiTabs-indicator': {
                height: 3,
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
                backgroundColor: theme.palette.primary.main
              },
              '& .MuiTab-root': {
                minHeight: 48,
                fontSize: '0.9rem',
                fontWeight: 600,
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  // Remove background color highlight
                  backgroundColor: 'transparent'
                },
                '&:hover': {
                  // Lighter hover effect
                  backgroundColor: alpha(theme.palette.primary.main, 0.03)
                }
              }
            }}
          >
            <Tab 
              label="Gallery" 
              icon={<ImageIcon sx={{ fontSize: '1.2rem' }} />} 
              iconPosition="start" 
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              label="Upload" 
              icon={<UploadIcon sx={{ fontSize: '1.2rem' }} />} 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
          
          <DialogContent 
            sx={{ 
              p: 0, // Remove default padding 
              display: 'flex', 
              flexDirection: 'column',
              flex: 1,
              overflowX: 'hidden', // Prevent horizontal scrolling
              backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fa'
            }}
          >
            {/* GALLERY TAB CONTENT */}
            <Box 
              sx={{ 
                display: tabValue === 0 ? 'flex' : 'none',
                flexDirection: 'column',
                flex: 1,
                height: '100%',
                width: '100%'
              }}
            >
            {error && (
                <Alert severity="error" sx={{ mx: 2.5, mt: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
                <Alert severity="success" sx={{ mx: 2.5, mt: 2 }}>
                {success}
              </Alert>
            )}
            
              {/* Controls Bar */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                px: 2.5,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : '#fff'
              }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {selectedDefectTypeId ? (
                    <>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        Showing {getFilteredPhotos().length} of {photos.length} photos with selected defect
                        <Button 
                          size="small" 
                          variant="outlined"
                          color="primary"
                          sx={{ ml: 1.5, textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                          onClick={() => setSelectedDefectTypeId(null)}
                        >
                          Clear filter
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
                      {photos.length > 0 && (
                        <>
                          {' • '}
                          <Tooltip title="Photos with annotations" arrow>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              <BorderColorIcon sx={{ fontSize: 14, mx: 0.5 }} />
                              {photos.filter(photo => photo.has_annotation === true).length}
                            </Box>
                          </Tooltip>
                        </>
                      )}
                    </>
                  )}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {galleryViewMode === 'card' && (
                    <>
                      <Button
                        size="small"
                        startIcon={<GridDensityIcon />}
                        onClick={handleDensityMenuOpen}
                        sx={{ 
                          minWidth: 'auto', 
                          textTransform: 'none',
                          fontWeight: 500,
                          px: 1.5,
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          }
                        }}
                      >
                        {cardsPerRow}
                      </Button>
                      <Menu
                        anchorEl={densityMenuAnchor}
                        open={Boolean(densityMenuAnchor)}
                        onClose={handleDensityMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        sx={{
                          '& .MuiPaper-root': {
                            borderRadius: 1.5,
                            boxShadow: theme.shadows[3],
                            border: '1px solid',
                            borderColor: theme.palette.divider
                          }
                        }}
                      >
                        <MenuItem 
                          onClick={() => handleCardsPerRowChange(2)}
                          selected={cardsPerRow === 2}
                          sx={{
                            minWidth: 150,
                            borderRadius: 1,
                            mx: 0.5,
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15)
                              }
                            }
                          }}
                        >
                          2 cards per row
                        </MenuItem>
                        <MenuItem 
                          onClick={() => handleCardsPerRowChange(3)}
                          selected={cardsPerRow === 3}
                          sx={{
                            minWidth: 150,
                            borderRadius: 1,
                            mx: 0.5,
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15)
                              }
                            }
                          }}
                        >
                          3 cards per row
                        </MenuItem>
                        <MenuItem 
                          onClick={() => handleCardsPerRowChange(4)}
                          selected={cardsPerRow === 4}
                          sx={{
                            minWidth: 150,
                            borderRadius: 1,
                            mx: 0.5,
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15)
                              }
                            }
                          }}
                        >
                          4 cards per row
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                  
                  <ToggleButtonGroup
                    value={galleryViewMode}
                    exclusive
                    onChange={handleGalleryViewChange}
                    aria-label="gallery view mode"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 1.5,
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        px: 1.5,
                        '&:not(:first-of-type)': {
                          borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        },
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.25)
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="card" aria-label="card view">
                      <ViewModuleIcon />
                    </ToggleButton>
                    <ToggleButton value="list" aria-label="list view">
                      <ViewListIcon />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {/* Selection Mode Toggle for Multi-Delete */}
                  <Tooltip title={selectionModeForDelete ? "Exit selection mode" : "Select photos to delete"}>
                    <IconButton
                      size="small"
                      color={selectionModeForDelete ? "error" : "default"}
                      onClick={toggleDeleteSelectionMode}
                      sx={{
                        bgcolor: selectionModeForDelete
                          ? alpha(theme.palette.error.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 1.5,
                        '&:hover': {
                          bgcolor: selectionModeForDelete
                            ? alpha(theme.palette.error.main, 0.2)
                            : alpha(theme.palette.primary.main, 0.1),
                        }
                      }}
                    >
                      {selectionModeForDelete ? <CloseIcon fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Bulk Actions Bar - shown when photos are selected */}
              {selectionModeForDelete && getSelectedDeleteCount() > 0 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.5,
                  py: 1,
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.error.main, 0.2)
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getSelectedDeleteCount()} photo{getSelectedDeleteCount() > 1 ? 's' : ''} selected
                  </Typography>
                  <Button size="small" onClick={selectAllPhotosForDelete} sx={{ textTransform: 'none', minWidth: 'auto' }}>
                    Select All
                  </Button>
                  <Button size="small" onClick={deselectAllPhotosForDelete} sx={{ textTransform: 'none', minWidth: 'auto' }}>
                    Deselect All
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirmOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Delete Selected
                  </Button>
                </Box>
              )}

              <Box sx={{ 
                p: 2.5, 
                flex: 1, 
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
              {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 10 }}>
                    <CircularProgress size={32} thickness={4} />
                </Box>
              ) : filteredPhotos.length > 0 ? (
                  <>
                    {/* Card View */}
                    {galleryViewMode === 'card' && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        justifyContent: 'flex-start',
                        width: '100%', // Ensure it takes full width
                        '& > *': { 
                          flexGrow: 0,
                          flexShrink: 0 
                        }
                      }}>
                  {getFilteredPhotos().map((photo, index) => {
                    const isDrone = hasDroneData(photo);
                    const isExpanded = expandedCards[photo.id] || false;
                    const cardWidth = getCardWidth();
                    const hasAnnotation = photo.has_annotation === true;
                    
                    return (
                            <Card
                              key={photo.id || index}
                              sx={{
                                width: cardWidth,
                                height: 380,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                boxSizing: 'border-box',
                                boxShadow: theme.shadows[2],
                                transition: 'box-shadow 0.2s, transform 0.2s',
                                '&:hover': {
                                  boxShadow: theme.shadows[6],
                                  transform: 'translateY(-4px)'
                                },
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                position: 'relative' // Added to allow absolute positioning of children
                              }}
                            >
                          <AuthenticatedImage
                            src={photo.url}
                            alt={`Inspection photo ${index + 1}`}
                            sx={{
                              height: 180,
                              objectFit: 'cover',
                              flexShrink: 0,
                              cursor: 'pointer'
                            }}
                            onClick={() => onPhotoSelect && onPhotoSelect(photo, getFilteredPhotos(), getFilteredPhotos().findIndex(p => p.id === photo.id))}
                          />
                          
                          {/* Delete Button Overlay - hidden in selection mode */}
                          {!selectionModeForDelete && (
                            <Tooltip title="Delete photo" arrow placement="left">
                              <IconButton
                                size="small"
                                onClick={(e) => openDeleteConfirm(photo, e)}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  zIndex: 2,
                                  bgcolor: alpha(theme.palette.error.main, 0.9),
                                  color: '#fff',
                                  '&:hover': {
                                    bgcolor: theme.palette.error.main,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s',
                                  boxShadow: theme.shadows[2]
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Checkbox overlay for multi-select deletion */}
                          {selectionModeForDelete && (
                            <Checkbox
                              checked={!!selectedPhotosForDelete[photo.id]}
                              onChange={() => togglePhotoForDelete(photo.id)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                zIndex: 2,
                                bgcolor: 'rgba(255,255,255,0.9)',
                                borderRadius: 1,
                                p: 0.25,
                                '&:hover': {
                                  bgcolor: 'rgba(255,255,255,1)',
                                },
                                '&.Mui-checked': {
                                  color: theme.palette.error.main
                                }
                              }}
                            />
                          )}

                          {/* Annotation Status Indicator - REVISED */}
                          <Tooltip title={hasAnnotation ? `Contains ${photo.annotation_count || 0} annotations` : "No annotations"} arrow placement="top">
                            <Box 
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                // Use more appropriate colors - blue for annotations, light gray for none
                                bgcolor: hasAnnotation 
                                  ? alpha(theme.palette.info.main, 0.9) 
                                  : alpha(theme.palette.grey[400], 0.7),
                                color: '#fff',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                p: '3px 6px',
                                fontSize: '0.7rem',
                                fontWeight: 'medium',
                                boxShadow: theme.shadows[2],
                                border: '1px solid',
                                borderColor: hasAnnotation
                                  ? alpha(theme.palette.info.dark, 0.3)
                                  : alpha(theme.palette.grey[500], 0.2),
                                pointerEvents: 'none' // Make it non-interactive to appear less like a button
                              }}
                            >
                              <BorderColorIcon sx={{ fontSize: 12, mr: 0.5 }} />
                              {hasAnnotation ? (photo.annotation_count || '0') : '0'}
                            </Box>
                          </Tooltip>

                              <CardContent 
                                sx={{
                                  p: 1.5,
                                  height: 200,
                                  overflow: 'auto',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flexShrink: 0,
                                  '&:last-child': { pb: 1.5 },
                                  borderTop: '1px solid',
                                  borderColor: alpha(theme.palette.divider, 0.5),
                                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : '#fff'
                                }}
                              >
                            {isDrone && (
                              <Box sx={{ mb: 0.5 }}>
                                <Chip 
                                  icon={<FlightIcon />} 
                                  label={`${photo.drone_make || 'Drone'} ${photo.drone_model || ''}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              </Box>
                            )}
                            
                            {photo.caption && (
                                  <Tooltip title={photo.caption} arrow>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="medium"
                                      sx={{ 
                                        mb: 0.5, 
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                    >
                                      {formatCaption(photo.caption)}
                          </Typography>
                                  </Tooltip>
                            )}
                            
                            {(photo.latitude || photo.longitude) && (
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mt: 0.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                borderRadius: 1,
                                p: 0.5,
                                pl: 1
                              }}>
                                <LocationOnIcon color="primary" sx={{ fontSize: 16, mr: 0.5 }} />
                                <Typography variant="caption" color="text.primary" fontWeight="medium">
                                  {photo.latitude ? photo.latitude.toFixed(6) : '?'}, 
                                  {photo.longitude ? photo.longitude.toFixed(6) : '?'}
                                  {photo.altitude ? ` @ ${photo.altitude.toFixed(1)}m` : ''}
                                </Typography>
                              </Box>
                            )}
                            
                            {isDrone && (
                                  <Box sx={{ mt: 0.5 }}>
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    p: 0.5,
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                  }}
                                  onClick={() => toggleDroneData(photo.id)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FlightIcon 
                                      color="info" 
                                      sx={{ 
                                        fontSize: 16, 
                                        mr: 0.5,
                                        transform: 'rotate(45deg)', // Tilt the plane icon up to indicate flight
                                        opacity: 0.9,
                                        mt: '1px', // Slight adjustment to align with text
                                        display: 'flex'
                                      }} 
                                    />
                                    <Typography 
                                      variant="caption" 
                                      color="text.primary" 
                                      fontWeight="medium"
                                      sx={{ 
                                        lineHeight: 1.2,
                                        mt: '1px', // Slight adjustment for perfect alignment
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      Drone Flight Data
                                    </Typography>
                                  </Box>
                                  <IconButton size="small" sx={{ p: 0 }}>
                                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                  </IconButton>
                                </Box>
                                
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ pt: 1, pb: 0, px: 0.5, maxHeight: 100, overflowY: 'auto' }}>
                                        {/* Drone details - simplified for brevity */}
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 'bold' }}>
                                          Flight Data
                                    </Typography>
                                    <Grid container spacing={1} sx={{ mb: 1 }}>
                                      <Grid item xs={4}>
                                        <Typography variant="caption" display="block">
                                          Yaw: {photo.flight_yaw !== null ? photo.flight_yaw.toFixed(1) + '°' : 'N/A'}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={4}>
                                        <Typography variant="caption" display="block">
                                          Pitch: {photo.flight_pitch !== null ? photo.flight_pitch.toFixed(1) + '°' : 'N/A'}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={4}>
                                        <Typography variant="caption" display="block">
                                          Roll: {photo.flight_roll !== null ? photo.flight_roll.toFixed(1) + '°' : 'N/A'}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                        {/* Additional drone data could go here */}
                                  </Box>
                                </Collapse>
                                  </Box>
                            )}
                            
                                <Box sx={{ mt: 'auto', pt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  {/* Moved ID and Added Date */}
                                  <Box>
                                    {photo.taken_at && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                                        <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, color: theme.palette.info.main }} />
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(photo.taken_at).toLocaleDateString(undefined, {
                                            year: 'numeric',
                                            month: 'short',
                                                  day: 'numeric'
                                          })}
                                        </Typography>
                                      </Box>
                                    )}
                                    <Typography variant="caption" color="text.disabled">
                                      ID: {photo.id || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    )}


                    {/* List View */}
                    {galleryViewMode === 'list' && (
                      <Box sx={{ 
                        width: '100%', 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1
                      }}>
                        {/* Table Header */}
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: selectionModeForDelete ? '40px 72px 1fr 180px 180px 120px 60px' : '72px 1fr 180px 180px 120px 60px',
                          alignItems: 'center',
                          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.light, 0.1),
                          borderBottom: '2px solid',
                          borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : theme.palette.primary.light,
                          px: 2,
                          py: 1.25,
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                        }}>
                          {selectionModeForDelete && (
                            <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase', textAlign: 'center' }}>

                            </Typography>
                          )}
                          <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                            Thumbnail
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                            File Name
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                            Location
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase' }}>
                            Date
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase', textAlign: 'center' }}>
                            Type / Status
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.dark', textTransform: 'uppercase', textAlign: 'center' }}>
                            Actions
                          </Typography>
                        </Box>
                        
                        {/* Table Body */}
                        <Box sx={{ 
                          flex: 1,
                          overflow: 'auto',
                          '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: alpha(theme.palette.grey[200], 0.5),
                          },
                        }}>
                          {getFilteredPhotos().map((photo, index) => {
                            const isDrone = hasDroneData(photo);
                            const hasAnnotation = photo.has_annotation === true;
                            
                            // Format location data
                            let locationText = 'N/A';
                            if (photo.latitude && photo.longitude) {
                              locationText = `${photo.latitude.toFixed(4)}, ${photo.longitude.toFixed(4)}`;
                              if (photo.altitude) {
                                locationText += ` @ ${photo.altitude.toFixed(0)}m`;
                              }
                            }
                            
                            // Format date
                            const dateText = photo.taken_at 
                              ? new Date(photo.taken_at).toLocaleDateString(undefined, { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'Unknown';
                            
                            return (
                              <Box
                                key={photo.id || index}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: selectionModeForDelete ? '40px 72px 1fr 180px 180px 120px 60px' : '72px 1fr 180px 180px 120px 60px',
                                  alignItems: 'center',
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  px: 2,
                                  py: 1,
                                  transition: 'all 0.2s',
                                  bgcolor: selectedPhotosForDelete[photo.id]
                                    ? alpha(theme.palette.error.main, 0.08)
                                    : (index % 2 === 0
                                      ? (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff')
                                      : (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.grey[50], 0.5))),
                                  '&:hover': {
                                    bgcolor: selectedPhotosForDelete[photo.id]
                                      ? alpha(theme.palette.error.main, 0.12)
                                      : (theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.primary.main, 0.1)
                                        : alpha(theme.palette.primary.light, 0.1)),
                                  }
                                }}
                              >
                                {/* Checkbox for selection mode */}
                                {selectionModeForDelete && (
                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Checkbox
                                      checked={!!selectedPhotosForDelete[photo.id]}
                                      onChange={() => togglePhotoForDelete(photo.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{
                                        p: 0.5,
                                        '&.Mui-checked': {
                                          color: theme.palette.error.main
                                        }
                                      }}
                                    />
                                  </Box>
                                )}
                                {/* Thumbnail */}
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 45,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    boxShadow: 1,
                                    cursor: 'pointer' // Add cursor pointer
                                  }}
                                  onClick={() => onPhotoSelect && onPhotoSelect(photo)} // Add onClick handler
                                >
                                  <AuthenticatedImage
                                    src={photo.url}
                                    alt={`Thumbnail ${index + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      transition: 'transform 0.3s ease',
                                      '&:hover': {
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                    onClick={() => onPhotoSelect && onPhotoSelect(photo)}
                                  />
                                </Box>
                                
                                {/* File Name */}
                                <Box sx={{ 
                                  pl: 2, 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  <Tooltip title={photo.caption || `Photo ${index + 1}`}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {formatCaption(photo.caption) || `Photo ${index + 1}`}
                                    </Typography>
                                  </Tooltip>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                ID: {photo.id || 'N/A'}
                              </Typography>
                                </Box>
                                
                                {/* Location */}
                                <Box>
                                  {photo.latitude && photo.longitude ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <LocationOnIcon 
                                        color="primary" 
                                        sx={{ fontSize: 16, mr: 0.5 }} 
                                      />
                                      <Tooltip title={locationText}>
                                        <Typography variant="caption">
                                          {locationText}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                  ) : (
                                <Typography variant="caption" color="text.disabled">
                                      No location data
                                </Typography>
                              )} 
                            </Box>
                                
                                {/* Date */}
                                <Box>
                                  {photo.taken_at ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <CalendarTodayIcon 
                                        sx={{ fontSize: 14, mr: 0.5, color: theme.palette.info.main }} 
                                      />
                                      <Typography variant="caption">
                                        {dateText}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">
                                      Unknown date
                                    </Typography>
                                  )}
                                </Box>
                                
                                {/* Type / Status */}
                                <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {isDrone ? (
                                    <Tooltip title="Drone Photo">
                                      <Chip
                                        icon={<FlightIcon fontSize="small" />}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', p: 0.5, minWidth: 0, height: 22 }}
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Standard Photo">
                                      <Chip
                                        icon={<CameraIcon fontSize="small" />}
                                        size="small"
                                        variant="outlined"
                                        color="default"
                                        sx={{ fontSize: '0.7rem', p: 0.5, minWidth: 0, height: 22 }}
                                      />
                                    </Tooltip>
                                  )}
                                  {/* Updated Annotation Indicator */}
                                  <Tooltip title={hasAnnotation ? `Contains ${photo.annotation_count || 0} annotations` : "No annotations"} arrow>
                                    <Chip
                                      icon={<BorderColorIcon fontSize="small" />}
                                      label={hasAnnotation ? (photo.annotation_count || '0') : '0'}
                                      size="small"
                                      variant="outlined"
                                      color={hasAnnotation ? "info" : "default"}
                                      sx={{ 
                                        fontSize: '0.7rem', 
                                        height: 22,
                                        opacity: hasAnnotation ? 1 : 0.6,
                                        '& .MuiChip-label': {
                                          px: 0.5,
                                          fontSize: '0.7rem'
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                </Box>
                                
                                {/* Actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Tooltip title="Delete photo">
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={(e) => openDeleteConfirm(photo, e)}
                                      sx={{
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.error.main, 0.1),
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                    );
                  })}
                        </Box>
                      </Box>
                    )}
                  </>
              ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center',
                    alignItems: 'center', 
                    height: '100%', 
                    py: 8,
                    color: 'text.secondary'
                  }}>
                    {selectedDefectTypeId && photos.length > 0 ? (
                      <>
                        <BugReportIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                        <Typography variant="h6">No Photos With Selected Defect</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          None of the photos contain the selected defect type.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setSelectedDefectTypeId(null)}
                          sx={{ mt: 3 }}
                        >
                          Show All Photos
                        </Button>
                      </>
                    ) : (
                      <>
                        <ImageIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                        <Typography variant="h6">No Photos Available</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          This inspection doesn't have any photos yet.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<UploadIcon />}
                          onClick={() => setTabValue(1)}
                          sx={{ mt: 3 }}
                        >
                          Upload Photos
                        </Button>
                      </>
                    )}
                  </Box>
              )}
              </Box>
            </Box>
            
            {/* UPLOAD TAB CONTENT */}
            <Box 
              sx={{ 
                display: tabValue === 1 ? 'flex' : 'none',
                flexDirection: 'column',
                p: 2.5,
                flex: 1,
                height: '100%',
                width: '100%'
              }}
            >
              <Paper
                {...getRootProps()}
                sx={{
                  p: 4,
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  mb: 3
                }}
              >
                <input {...getInputProps()} />
                <Box sx={{ textAlign: 'center' }}>
                  <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isDragActive ? 'Drop images here' : 'Drag & Drop'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isDragActive
                      ? 'Release to add images'
                      : 'Drop image files here, or click to select files'}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                    Accepts JPG, PNG and GIF images up to 20MB
                  </Typography>
                </Box>
              </Paper>
              
              {uploadError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {uploadError}
                </Alert>
              )}
              
              {uploading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Uploading... {uploadProgress}%
                  </Typography>
                  <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        height: 8,
                        width: `${uploadProgress}%`,
                        bgcolor: 'primary.main',
                        transition: 'width 0.2s ease'
                      }}
                    />
                  </Box>
                </Box>
              )}
              
              {uploadFiles.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {uploadFiles.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius: 1,
                          height: 200
                        }}
                      >
                        <Box
                          component="img"
                          src={file.preview}
                          alt={`Preview ${index}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(255,0,0,0.7)',
                            }
                          }}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </DialogContent>
        </>
      ) : reportView ? (
        <DialogContent 
          sx={{ 
            p: 0, 
            display: 'flex', 
            flexDirection: 'column',
            flex: 1,
            backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fa'
          }}
        >
          {!reportPreviewMode ? (
            /* Original Report View Content */
            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Report Header */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  border: '1px solid', 
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  {generateReportTitle()}
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Inspection Details</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {inspection?.title || 'Untitled Inspection'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Date: {inspection?.date ? new Date(inspection.date).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A'}
                    </Typography>
                    {inspection?.cycle && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Cycle: {inspection.cycle}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Summary</Typography>
                    {defectReport.loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CircularProgress size={16} thickness={4} sx={{ mr: 1 }} />
                        <Typography variant="body2">Loading defect data...</Typography>
                      </Box>
                    ) : defectReport.error ? (
                      <Alert severity="error" sx={{ mt: 1, fontSize: '0.875rem' }}>
                        {defectReport.error}
                      </Alert>
                    ) : defectReport.data ? (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Total Defects:</strong> {
                            defectReport.data 
                              ? (defectReport.data.total || 
                                 (Array.isArray(defectReport.data.defects) ? defectReport.data.defects.length : 0))
                              : 0
                          }
                        </Typography>
                        
                        {/* Calculate number of images with defects */}
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Images with Defects:</strong> {
                            defectReport.data?.photosWithDefectsCount || 0
                          }
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Defect Types:</strong> {
                            defectReport.data 
                              ? (() => {
                                  // Get defect types count
                                  const typesCount = defectReport.data.defectTypes 
                                    ? defectReport.data.defectTypes.length 
                                    : (defectReport.data.defects 
                                       ? [...new Set(defectReport.data.defects
                                           .filter(d => d && d.defect_type_id)
                                           .map(d => d.defect_type_id))].length
                                       : 0);
                                
                                  // Get defect types details for inline display
                                  let typesDetails = '';
                                  if (typesCount > 0) {
                                    const defectTypes = defectReport.data.defectTypes || [];
                                    if (defectTypes.length > 0) {
                                      typesDetails = ` (${defectTypes
                                        .map(type => `${type.defect_type_name.toLowerCase()}: ${type.count}`)
                                        .join(', ')})`;
                                    } else if (defectReport.data.defects && defectReport.data.defects.length > 0) {
                                      // Calculate types from defects if defectTypes not available
                                      const typesMap = {};
                                      defectReport.data.defects
                                        .filter(d => d && d.defect_type_id && d.defect_type_name)
                                        .forEach(d => {
                                          const typeId = d.defect_type_id;
                                          if (!typesMap[typeId]) {
                                            typesMap[typeId] = {
                                              name: d.defect_type_name.toLowerCase(),
                                              count: 0
                                            };
                                          }
                                          typesMap[typeId].count++;
                                        });
                                      
                                      const typesList = Object.values(typesMap);
                                      if (typesList.length > 0) {
                                        typesDetails = ` (${typesList
                                          .map(type => `${type.name}: ${type.count}`)
                                          .join(', ')})`;
                                      }
                                    }
                                  }
                                  
                                  return `${typesCount}${typesDetails}`;
                                })()
                            : 0
                        }
                        </Typography>

                        {/* Live post-exclusion summary - only when user has unticked at least one finding */}
                        {findingsExcluded > 0 && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.warning.main, 0.08),
                              color: theme.palette.warning.dark,
                              fontSize: '0.8rem',
                            }}
                          >
                            <strong>After exclusion:</strong> {findingsSelected} defect{findingsSelected === 1 ? '' : 's'} across {
                              new Set(
                                sortedDefectsForReview
                                  .filter(d => !excludedDefectIds.has(String(d.id)))
                                  .map(d => d.photo_path)
                              ).size
                            } photo{
                              (new Set(
                                sortedDefectsForReview
                                  .filter(d => !excludedDefectIds.has(String(d.id)))
                                  .map(d => d.photo_path)
                              ).size) === 1 ? '' : 's'
                            } will be included in the PDF.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No defect data available
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Findings Review - one row per defect, grouped by photo, with checkbox to exclude */}
              {sortedDefectsForReview.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                  }}
                >
                  {/* Header: title + status + bulk actions */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        Findings Review
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Untick any finding you want to exclude from the generated PDF.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${findingsSelected} of ${findingsTotal} selected`}
                        />
                        {findingsExcluded > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${findingsExcluded} excluded from PDF`}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectAllDefects}
                        disabled={findingsExcluded === 0}
                        sx={{ textTransform: 'none' }}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={handleDeselectAllDefects}
                        disabled={findingsSelected === 0}
                        sx={{ textTransform: 'none' }}
                      >
                        Deselect All
                      </Button>
                    </Box>
                  </Box>

                  {/* List of defects */}
                  <List
                    dense
                    disablePadding
                    sx={{
                      maxHeight: 380,
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      borderRadius: 1,
                    }}
                  >
                    {sortedDefectsForReview.map((defect, idx) => {
                      const idKey = String(defect.id);
                      const isExcluded = excludedDefectIds.has(idKey);
                      const photoPath = (defect.photo_path || '').replace(/\\/g, '/');
                      const photoEntry = photoByPath.get(defect.photo_path);
                      const photoIndex = photoEntry?.index;
                      const photoCaption = photoEntry?.photo?.caption;
                      const isLast = idx === sortedDefectsForReview.length - 1;

                      // Bounding-box data for hover preview
                      const bbox = defect.bounding_box;
                      const ow = defect.image_width || 1;
                      const oh = defect.image_height || 1;
                      const hasBbox = bbox && bbox.x1 != null && bbox.y1 != null && bbox.x2 != null && bbox.y2 != null;

                      const hoverPreview = (
                        <Box sx={{ width: 260, p: 0.5 }}>
                          {photoPath && (
                            <Box sx={{ position: 'relative', width: '100%', mb: 1, borderRadius: 0.5, overflow: 'hidden', lineHeight: 0 }}>
                              <AuthenticatedImage
                                src={photoPath}
                                alt={`Defect ${idx + 1}`}
                                sx={{ width: '100%', display: 'block', borderRadius: '4px' }}
                              />
                              {hasBbox && (
                                <Box sx={{
                                  position: 'absolute',
                                  left: `${(parseFloat(bbox.x1) / ow) * 100}%`,
                                  top: `${(parseFloat(bbox.y1) / oh) * 100}%`,
                                  width: `${((parseFloat(bbox.x2) - parseFloat(bbox.x1)) / ow) * 100}%`,
                                  height: `${((parseFloat(bbox.y2) - parseFloat(bbox.y1)) / oh) * 100}%`,
                                  border: '2px solid #ff1744',
                                  boxSizing: 'border-box',
                                  pointerEvents: 'none',
                                }} />
                              )}
                            </Box>
                          )}
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#fff', mb: 0.25 }}>
                            #{idx + 1} — {defect.defect_type_name || 'Unknown'}
                          </Typography>
                          {defect.severity_level_name && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.8)' }}>
                              Severity: {defect.severity_level_name}
                            </Typography>
                          )}
                          {defect.status && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.8)' }}>
                              Status: {defect.status}
                            </Typography>
                          )}
                          {defect.description && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', mt: 0.5, fontStyle: 'italic' }} noWrap>
                              {defect.description}
                            </Typography>
                          )}
                        </Box>
                      );

                      return (
                        <Tooltip
                          key={idKey}
                          title={hoverPreview}
                          placement="right"
                          arrow
                          enterDelay={300}
                          enterNextDelay={150}
                          slotProps={{
                            tooltip: { sx: { maxWidth: 280, bgcolor: 'rgba(33,33,33,0.96)', p: 1, borderRadius: 1.5 } },
                            arrow: { sx: { color: 'rgba(33,33,33,0.96)' } },
                          }}
                        >
                        <ListItem
                          divider={!isLast}
                          sx={{
                            py: 1,
                            opacity: isExcluded ? 0.55 : 1,
                            backgroundColor: isExcluded
                              ? alpha(theme.palette.warning.main, 0.06)
                              : 'transparent',
                            transition: 'background-color 0.15s, opacity 0.15s',
                          }}
                          secondaryAction={
                            <Checkbox
                              edge="end"
                              size="small"
                              checked={!isExcluded}
                              onChange={() => toggleDefectExclusion(defect.id)}
                              inputProps={{
                                'aria-label': `Include defect ${defect.defect_type_name || ''} in PDF`,
                              }}
                            />
                          }
                        >
                          <ListItemAvatar>
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                bgcolor: theme.palette.grey[200],
                                cursor: 'pointer',
                                flexShrink: 0,
                              }}
                              onClick={() => {
                                const photo = photoEntry?.photo;
                                if (photo && onPhotoSelect) onPhotoSelect(photo);
                              }}
                            >
                              {photoPath ? (
                                <AuthenticatedImage
                                  src={photoPath}
                                  alt={`Photo ${photoIndex || ''}`}
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <ImageIcon fontSize="small" color="disabled" />
                                </Box>
                              )}
                            </Box>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Photo {photoIndex || '?'}
                                </Typography>
                                {photoCaption && (
                                  <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }} noWrap>
                                    — {photoCaption}
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  icon={<BugReportIcon sx={{ fontSize: 14 }} />}
                                  label={defect.defect_type_name || 'Unknown defect'}
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.primary.main, 0.25),
                                  }}
                                />
                                {defect.severity_level_name && (
                                  <Chip
                                    size="small"
                                    label={defect.severity_level_name}
                                    sx={{
                                      height: 22,
                                      fontSize: '0.7rem',
                                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                                      color: theme.palette.warning.dark,
                                      border: '1px solid',
                                      borderColor: alpha(theme.palette.warning.main, 0.3),
                                    }}
                                  />
                                )}
                                {isExcluded && (
                                  <Chip
                                    size="small"
                                    label="Excluded"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.7rem',
                                      bgcolor: alpha(theme.palette.error.main, 0.1),
                                      color: theme.palette.error.dark,
                                      border: '1px solid',
                                      borderColor: alpha(theme.palette.error.main, 0.3),
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            sx={{ ml: 1.5 }}
                          />
                        </ListItem>
                        </Tooltip>
                      );
                    })}
                  </List>

                  {findingsExcluded > 0 && (
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{ mt: 2, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                    >
                      {findingsExcluded} finding{findingsExcluded === 1 ? '' : 's'} will not be included when you click <strong>Preview PDF</strong>.
                    </Alert>
                  )}
                </Paper>
              )}
              
              {/* Loading state or placeholder */}
              {defectReport.loading ? (
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Loading inspection data...
                  </Typography>
                </Box>
              ) : !defectReport.data && !defectReport.error ? (
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                  <Typography variant="h6" color="text.secondary">
                    Click "Preview Report" to generate a full inspection report
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : (
            /* Report Preview Mode - PDF Report with PDFReport component */
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              bgcolor: '#fff',
              height: '100%',
              position: 'relative',
              p: 3
            }}>
              {/* Back button */}
              <Button
                variant="outlined"
                size="small"
                onClick={handleBackToReport}
                startIcon={<ChevronLeftIcon />}
                sx={{ 
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  zIndex: 10,
                  textTransform: 'none'
                }}
              >
                Back to Report
              </Button>

              {/* PDF Report Component */}
              <Box sx={{
                flex: 1,
                mt: 6,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <PDFReport
                  inspection={currentInspection ? {
                    ...currentInspection,
                    // Ensure date is populated correctly for the cover page "Generated: [Date]" field
                    // It looks for 'date' prop in PDFReport.js line 769: formatDate(inspection?.date)
                    date: currentInspection.inspection_date || currentInspection.date || new Date().toISOString()
                  } : null}
                  defectReport={defectReport}
                  photos={photos}
                  severityLevels={severityLevels} // Pass severityLevels down
                  assetType={currentAssetType} // Pass asset type for report saving
                  reportTitle={generateReportTitle()}
                  reportSummary={getReportSummary()} // Pass the summary object to enable the summary box
                  reportDate={reportDate}
                  assetDisplayImage={assetDetails?.display_image_path || null}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      ) : (
        /* AI Analysis View Content */
        <DialogContent 
          sx={{ 
            p: 0, 
            display: 'flex', 
            flexDirection: 'column',
            flex: 1,
            backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : '#f8f9fa'
          }}
        >
          {!selectingImages ? (
            // Normal AI Analysis View
            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* AI Analysis Header */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  mb: 3, 
                  border: '1px solid', 
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 2, fontSize: '1.125rem' }}>
                  AI Defect Analysis
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" fontSize="0.7rem" textTransform="uppercase" fontWeight={600} gutterBottom>
                      Inspection Details
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontSize: '0.95rem', fontWeight: 600 }}>
                      {inspection?.title || 'Untitled Inspection'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                      Date: {inspection?.date ? new Date(inspection.date).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A'}
                    </Typography>
                    {inspection?.cycle && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.85rem' }}>
                        Cycle: {inspection.cycle}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" fontSize="0.7rem" textTransform="uppercase" fontWeight={600} gutterBottom>
                      AI Summary
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span>Total Images:</span>
                        <span style={{ minWidth: '30px', textAlign: 'right' }}>{photos.length}</span>
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span>Images with AI Annotations:</span>
                        <span style={{ minWidth: '30px', textAlign: 'right' }}>{photos.filter(p => p.has_annotation).length}</span>
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5, display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span>Total AI Detections:</span>
                        <span style={{ minWidth: '30px', textAlign: 'right' }}>{getTotalAnnotationCount()}</span>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
              
              {/* AI Server Status Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  border: '1px solid', 
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                    AI Server Status
                  </Typography>
                    {lastRefreshTime && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Last updated: {lastRefreshTime.toLocaleDateString('en-GB')} at {lastRefreshTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      onClick={fetchAiData}
                      disabled={aiDataLoading}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <RefreshIcon 
                        sx={{ 
                          fontSize: 18,
                          color: theme.palette.primary.main,
                          animation: aiDataLoading ? 'spin 1s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': {
                              transform: 'rotate(0deg)',
                            },
                            '100%': {
                              transform: 'rotate(360deg)',
                            },
                          },
                        }} 
                      />
                    </IconButton>
                    {aiDataLoading && (
                      <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
                    )}
                  </Box>
                </Box>
                
                {aiDataLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : aiDataError ? (
                  <Alert severity="error">{aiDataError}</Alert>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { 
                        xs: '1fr', 
                        sm: 'repeat(2, 1fr)', 
                        md: 'repeat(3, 1fr)', 
                        lg: 'repeat(6, 1fr)'
                      },
                      gap: 2
                    }}
                  >
                    {/* API Status Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1.5,
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: aiServerStatus?.api_server_status === 'Online' 
                          ? alpha(theme.palette.success.main, 0.05)
                          : alpha(theme.palette.error.main, 0.05)
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: aiServerStatus?.api_server_status === 'Online'
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          flexShrink: 0
                        }}
                      >
                        {aiServerStatus?.api_server_status === 'Online' ? (
                          <CloudDoneIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                        ) : (
                          <CloudOffIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                        )}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                        API Server
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.75rem', 
                          fontWeight: 600,
                          color: aiServerStatus?.api_server_status === 'Online' 
                            ? theme.palette.success.main 
                            : theme.palette.error.main
                        }}
                      >
                        {aiServerStatus?.api_server_status || 'Offline'}
                      </Typography>
                    </Paper>

                    {/* Processor Status Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1.5,
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: aiServerStatus?.ai_processor_status === 'Online' 
                          ? alpha(theme.palette.success.main, 0.05)
                          : alpha(theme.palette.error.main, 0.05)
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: aiServerStatus?.ai_processor_status === 'Online'
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          flexShrink: 0
                        }}
                      >
                        <HubIcon sx={{ 
                          color: aiServerStatus?.ai_processor_status === 'Online' 
                            ? theme.palette.success.main 
                            : theme.palette.error.main, 
                          fontSize: 20 
                        }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                        AI Processor
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.75rem', 
                          fontWeight: 600,
                          color: aiServerStatus?.ai_processor_status === 'Online' 
                            ? theme.palette.success.main 
                            : theme.palette.error.main
                        }}
                      >
                        {aiServerStatus?.ai_processor_status || 'Offline'}
                      </Typography>
                    </Paper>
                    
                    {/* Health Summary Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1.5,
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: alpha(theme.palette.info.main, 0.05)
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          flexShrink: 0
                        }}
                      >
                        <WarningIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                        Health Summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {aiServerStatus?.health_summary || 'N/A'}
                      </Typography>
                    </Paper>
                    
                    {/* Active Jobs Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1.5,
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: alpha(theme.palette.secondary.main, 0.05)
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          flexShrink: 0
                        }}
                      >
                        <PlaylistPlayIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                        Active Jobs
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', color: theme.palette.secondary.main }}>
                        {aiServerStatus?.active_inference_jobs ?? '0'}
                      </Typography>
                    </Paper>
                    
                    {/* CPU/RAM Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1.5,
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          flexShrink: 0
                        }}
                      >
                        <DeveloperBoardIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                        CPU / RAM
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                          CPU: {aiServerStatus?.cpu_load_percent || '0'}%
                        </Box>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                          RAM: {aiServerStatus?.ram_load_percent || '0'}%
                        </Box>
                      </Typography>
                    </Paper>
                    
                    {/* GPU Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1.5,
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: alpha(theme.palette.warning.main, 0.05)
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          flexShrink: 0
                        }}
                      >
                        <MemoryIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                        GPU Usage
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                          Load: {aiServerStatus?.gpu_load_percent || '0'}%
                        </Box>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                          Memory: {aiServerStatus?.gpu_memory_percent ? parseFloat(aiServerStatus.gpu_memory_percent).toFixed(1) : '0.0'}%
                        </Box>
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Paper>
              
              {/* Model Coverage Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  AI Model Coverage
                </Typography>
                
                <Divider sx={{ mb: 2 }} />
                
                {aiDataLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : aiDataError ? (
                  <Alert severity="error">{aiDataError}</Alert>
                ) : (() => {
                  // Find all models that match the current asset type and sort by version (latest first)
                  const parseVersion = (version) => {
                    if (!version) return [0, 0, 0];
                    const parts = String(version).split('.').map(Number);
                    return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
                  };
                  const matchingModels = (aiModelDetails?.models?.filter(model =>
                    model.model_type === currentAssetType
                  ) || []).sort((a, b) => {
                    const [aMajor, aMinor, aPatch] = parseVersion(a.model_version);
                    const [bMajor, bMinor, bPatch] = parseVersion(b.model_version);
                    if (bMajor !== aMajor) return bMajor - aMajor;
                    if (bMinor !== aMinor) return bMinor - aMinor;
                    return bPatch - aPatch;
                  });

                  // Use selected model or fallback to latest matching model
                  const currentModel = selectedModel || matchingModels[0];
                  
                  return (
                    <>
                      {currentAssetType && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
                              Asset Type: {currentAssetType}
                            </Typography>
                            
                            {matchingModels.length > 1 ? (
                              <>
                                <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                  💡 Multiple AI models available - select your preferred model:
                                </Typography>
                                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                  <InputLabel id="ai-model-select-label">Select AI Model</InputLabel>
                                  <Select
                                    labelId="ai-model-select-label"
                                    id="ai-model-select"
                                    value={selectedModel?.model_id || ''}
                                    label="Select AI Model"
                                    onChange={(e) => {
                                      const selected = matchingModels.find(m => m.model_id === e.target.value);
                                      setSelectedModel(selected);
                                    }}
                                    sx={{ 
                                      bgcolor: 'background.paper',
                                      '& .MuiSelect-select': {
                                        fontSize: '0.875rem'
                                      }
                                    }}
                                  >
                                    {matchingModels.map((model, index) => (
                                      <MenuItem key={model.model_id} value={model.model_id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                          <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {model.description} v{model.model_version}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {Object.keys(model.classes).length} defect types • ID: {model.model_id}
                                            </Typography>
                                          </Box>
                                          {index === 0 && (
                                            <Chip label="Latest" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                          )}
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </>
                            ) : currentModel ? (
                              <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.secondary', mt: 1 }}>
                                Using: {currentModel.description} v{currentModel.model_version}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 400, color: 'warning.main', mt: 1 }}>
                                No matching AI model found
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {currentModel?.classes ? (
                  <Grid container spacing={2}>
                          {Object.values(currentModel.classes).map((className, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            height: '100%'
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: index % 4 === 0 ? theme.palette.success.main
                                : index % 4 === 1 ? theme.palette.info.main
                                : index % 4 === 2 ? theme.palette.warning.main
                                : theme.palette.error.main,
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {className}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                                             ) : currentAssetType ? (
                         <Alert severity="warning" sx={{ mt: 2 }}>
                           No AI model found for asset type "{currentAssetType}". 
                           Available models: {aiModelDetails?.models?.map(m => m.model_type).join(', ') || 'None'}
                         </Alert>
                      ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Unable to determine asset type. Please ensure the inspection has a valid asset associated.
                        </Alert>
                      )}
                    </>
                  );
                })()}
                
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                  <InfoIcon color="info" />
                  <Typography variant="body2">
                    This AI model is trained to detect up to {selectedModel?.classes ? Object.keys(selectedModel.classes).length : 0} types of defects for this asset type. Runtime: 0.3-0.7s per image.
                  </Typography>
                </Box>
              </Paper>
              
              {/* AI Processing Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 0, 
                  border: '1px solid', 
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                  AI Processing
                </Typography>
                
                {selectedModel && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2) }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.dark, fontSize: '0.85rem' }}>
                      ✓ Selected Model: {selectedModel.description} v{selectedModel.model_version}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Model ID: {selectedModel.model_id} • {Object.keys(selectedModel.classes).length} defect types
                    </Typography>
                  </Box>
                )}
                
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* AI Job Status Display */}
                  {(aiJob.status === 'SUBMITTING' || aiJob.status === 'PENDING' || aiJob.status === 'PROGRESS') && (
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderColor: 'primary.main' }}>
                      <CircularProgress size={24} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {aiJob.status === 'SUBMITTING' && 'Submitting Job...'}
                          {aiJob.status === 'PENDING' && aiJob.jobs && aiJob.jobs.length > 0 && (
                            `Processing Batch ${aiJob.jobs[aiJob.currentBatchIndex]?.batch_number || 1} of ${aiJob.jobs[0]?.total_batches || 1} - Queued`
                          )}
                          {aiJob.status === 'PROGRESS' && aiJob.jobs && aiJob.jobs.length > 0 && (
                            `Batch ${aiJob.jobs[aiJob.currentBatchIndex]?.batch_number || 1} of ${aiJob.jobs[0]?.total_batches || 1}: Image ${aiJob.jobs[aiJob.currentBatchIndex]?.progress?.current || 1} of ${aiJob.jobs[aiJob.currentBatchIndex]?.batch_size || 1}`
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {aiJob.status === 'PROGRESS' && aiJob.jobs[aiJob.currentBatchIndex]?.progress?.filename && (
                            `File: ${aiJob.jobs[aiJob.currentBatchIndex].progress.filename}`
                          )}
                          {(aiJob.status === 'SUBMITTING' || aiJob.status === 'PENDING') && 
                            'This may take several minutes. You can close this window; the process will continue.'}
                        </Typography>
                        {aiJob.jobs && aiJob.jobs.length > 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {aiJob.currentBatchIndex > 0 && `${aiJob.currentBatchIndex} batch(es) completed • `}
                            {aiJob.jobs.length - aiJob.currentBatchIndex - 1 > 0 && `${aiJob.jobs.length - aiJob.currentBatchIndex - 1} batch(es) remaining`}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  )}

                  {aiJob.status === 'SUCCESS' && (
                    <Alert 
                      severity="success" 
                      sx={{ 
                        '& .MuiAlert-message': { width: '100%' },
                        borderColor: 'success.main'
                      }}
                      icon={<CheckCircleIcon />}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                            ✨ AI Analysis Completed Successfully!
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {aiJob.allResults.length} images processed
                            {aiJob.jobs.length > 1 && ` in ${aiJob.jobs.length} batches`}
                            {' • '}Completed at {new Date().toLocaleTimeString()}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => setAiJob({
                            jobs: [],
                            currentBatchIndex: 0,
                            status: 'IDLE',
                            overallProgress: null,
                            error: null,
                            failedBatches: [],
                            allResults: []
                          })}
                          sx={{ textTransform: 'none', minWidth: 'auto' }}
                        >
                          Dismiss
                        </Button>
                      </Box>
                    </Alert>
                  )}

                  {aiJob.status === 'PARTIAL_SUCCESS' && (
                    <Alert 
                      severity="warning" 
                      sx={{ 
                        '& .MuiAlert-message': { width: '100%' },
                        borderColor: 'warning.main'
                      }}
                      icon={<WarningIcon />}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                            ⚠️ AI Analysis Partially Completed
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {aiJob.allResults.length} images processed • {aiJob.failedBatches.length} batch(es) failed
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setAiJob({
                            jobs: [],
                            currentBatchIndex: 0,
                            status: 'IDLE',
                            overallProgress: null,
                            error: null,
                            failedBatches: [],
                            allResults: []
                          })}
                          sx={{ textTransform: 'none', minWidth: 'auto' }}
                        >
                          Dismiss
                        </Button>
                      </Box>
                    </Alert>
                  )}

                  {aiJob.status === 'FAILED' && (
                    <Alert severity="error">
                      <strong>Analysis Failed:</strong> {aiJob.error}
                    </Alert>
                  )}

                  {photos.length > 150 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Too many images for AI analysis
                      </Typography>
                      <Typography variant="caption">
                        This inspection has {photos.length} images, but AI analysis is limited to 150 images maximum. 
                        Please reduce the number of images before running AI analysis.
                      </Typography>
                    </Alert>
                  )}
                  
                  <Button
                    variant="contained"
                    color={aiJob.status === 'SUCCESS' || aiJob.status === 'PARTIAL_SUCCESS' ? "success" : "primary"}
                    fullWidth
                    startIcon={aiJob.status === 'SUCCESS' || aiJob.status === 'PARTIAL_SUCCESS' ? <CheckCircleIcon /> : <AutoAwesomeIcon />}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      bgcolor: aiJob.status === 'SUCCESS' || aiJob.status === 'PARTIAL_SUCCESS' ? theme.palette.success.main : theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: aiJob.status === 'SUCCESS' || aiJob.status === 'PARTIAL_SUCCESS' ? theme.palette.success.dark : theme.palette.primary.dark,
                      },
                      borderRadius: 1.5
                    }}
                    onClick={handleRunAnalysis}
                    disabled={aiJob.status === 'SUBMITTING' || aiJob.status === 'PENDING' || aiJob.status === 'PROGRESS' || photos.length > 150}
                  >
                    {aiJob.status === 'SUBMITTING' && 'Submitting Job...'}
                    {aiJob.status === 'PENDING' && `Processing Batch ${(aiJob.currentBatchIndex || 0) + 1}...`}
                    {aiJob.status === 'PROGRESS' && aiJob.jobs && aiJob.jobs.length > 0 && (
                      `Batch ${aiJob.jobs[aiJob.currentBatchIndex]?.batch_number || 1}/${aiJob.jobs[0]?.total_batches || 1} Analyzing...`
                    )}
                    {(aiJob.status === 'SUCCESS' || aiJob.status === 'PARTIAL_SUCCESS') && '✅ Analysis Complete - Run Again'}
                    {(aiJob.status === 'IDLE' || aiJob.status === 'FAILED') && `Run AI Analysis on All Images${photos.length > 20 ? ` (${Math.ceil(photos.length / 20)} batches)` : ''}`}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<BorderColorIcon />}
                    onClick={toggleImageSelectionMode}
                    disabled
                    sx={{ 
                      py: 1.5, 
                      fontWeight: 600, 
                      borderRadius: 1.5,
                      opacity: 0.5,
                      cursor: 'not-allowed'
                    }}
                  >
                    Run AI Analysis on Selected Images
                  </Button>
                </Box>
                
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 1 }}>
                  <HelpIcon color="warning" />
                  <Typography variant="body2">
                    AI processing will automatically detect and annotate defects in your inspection images. The process may take several minutes depending on the number of images.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          ) : (
            // Image Selection View
            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Selection Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Select Images for AI Analysis
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={deselectAllImages}
                    disabled={getSelectedImagesCount() === 0 || isAnalyzing}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                  
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selectAllImages}
                    disabled={getSelectedImagesCount() === photos.length || isAnalyzing}
                    sx={{ textTransform: 'none' }}
                  >
                    Select All
                  </Button>
                </Box>
              </Box>
              
              {/* Selection Info */}
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                bgcolor: isAnalyzing 
                  ? alpha(theme.palette.warning.main, 0.1) 
                  : alpha(theme.palette.info.main, 0.1), 
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                {isAnalyzing ? (
                  <>
                    <AutoFixHighIcon color="warning" sx={{ animation: 'pulse 1.5s infinite' }} />
                    <Typography variant="body2">
                      AI analyzing {getSelectedImagesCount()} images. Please wait...
                    </Typography>
                  </>
                ) : (
                  <>
                    <InfoIcon color="info" />
                    <Typography variant="body2">
                      Selected {getSelectedImagesCount()} of {photos.length} images. Click on images to select/deselect them.
                    </Typography>
                  </>
                )}
              </Box>
              
              {/* Image Grid */}
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 2,
                '@keyframes sparkle': {
                  '0%': { transform: 'scale(0) rotate(0deg)', opacity: 0 },
                  '50%': { opacity: 1 },
                  '100%': { transform: 'scale(1.5) rotate(45deg)', opacity: 0 }
                },
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', opacity: 0.8 },
                  '50%': { transform: 'scale(1.2)', opacity: 1 },
                  '100%': { transform: 'scale(1)', opacity: 0.8 }
                },
                '@keyframes spinSparkle': {
                  '0%': { transform: 'rotate(0deg) scale(1)' },
                  '25%': { transform: 'rotate(90deg) scale(1.1)' },
                  '50%': { transform: 'rotate(180deg) scale(1)' },
                  '75%': { transform: 'rotate(270deg) scale(1.1)' },
                  '100%': { transform: 'rotate(360deg) scale(1)' }
                }
              }}>
                {photos.map(photo => (
                  <Paper
                    key={photo.id}
                    onClick={() => !isAnalyzing && toggleImageSelection(photo.id)}
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      cursor: isAnalyzing ? 'default' : 'pointer',
                      border: '3px solid',
                      borderColor: isImageSelected(photo.id) 
                        ? theme.palette.primary.main 
                        : 'transparent',
                      boxShadow: isImageSelected(photo.id) 
                        ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}` 
                        : theme.shadows[2],
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: !isAnalyzing ? 'translateY(-4px)' : 'none',
                        boxShadow: !isAnalyzing ? theme.shadows[5] : theme.shadows[2]
                      },
                      height: 180
                    }}
                  >
                    <AuthenticatedImage
                      src={photo.url}
                      alt={`Photo ${photo.id}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: isAnalyzing && isImageSelected(photo.id) && !analyzedImages[photo.id]
                          ? 'brightness(0.7)'
                          : 'none'
                      }}
                      onClick={() => onPhotoSelect && onPhotoSelect(photo)}
                    />
                    
                    {/* Selection Indicator */}
                    {isImageSelected(photo.id) && !isAnalyzing && (
                      <Box sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        boxShadow: theme.shadows[3]
                      }}>
                        <CheckIcon fontSize="small" />
                      </Box>
                    )}
                    
                    {/* AI Analyzing Animation - Updated with sparkle style from documentation */}
                    {isAnalyzing && isImageSelected(photo.id) && !analyzedImages[photo.id] && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(25, 118, 210, 0.1)',
                        overflow: 'hidden',
                        zIndex: 5
                      }}>
                        {/* Sparkle elements */}
                        {[...Array(6)].map((_, i) => (
                          <Box 
                            key={i}
                            sx={{
                              position: 'absolute',
                              width: 20,
                              height: 20,
                              background: 'radial-gradient(circle, #fff 10%, transparent 30%)',
                              animation: 'sparkle 2s infinite',
                              animationDelay: `${i * 0.3}s`,
                              top: [20, 60, 40, 70, 30, 80][i % 6] + '%',
                              left: [30, 70, 50, 20, 80, 40][i % 6] + '%',
                              '&::before, &::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                background: 'inherit'
                              },
                              '&::before': {
                                transform: 'rotate(45deg)'
                              },
                              '&::after': {
                                transform: 'rotate(90deg)'
                              }
                            }}
                          />
                        ))}
                        
                        {/* Central icon */}
                        <AutoAwesomeIcon 
                          sx={{ 
                            color: '#fff', 
                            fontSize: '40px',
                            filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.8))',
                            animation: 'pulse 2s infinite'
                          }} 
                        />
                      </Box>
                    )}
                    
                    {/* Completed Analysis Indicator */}
                    {isAnalyzing && isImageSelected(photo.id) && analyzedImages[photo.id] && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha('#000', 0.4),
                        zIndex: 5
                      }}>
                        <Box sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.success.main, 0.9),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 0 15px 3px ${alpha(theme.palette.success.main, 0.5)}`
                        }}>
                          <DoneIcon 
                            fontSize="medium" 
                            sx={{ 
                              color: 'white',
                              fontWeight: 'bold'
                            }} 
                          />
                        </Box>
                      </Box>
                    )}
                    
                    {/* Photo caption - bottom overlay */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      p: 0.75,
                      fontSize: '0.75rem',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}>
                      {photo.caption || `Photo ${photo.id}`}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      )}
      
      <DialogActions 
        sx={{ 
          p: 2, 
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#fff'
        }}
      >
        {selectingImages ? (
          // Image selection mode buttons
          <>
            <Button 
              onClick={toggleImageSelectionMode} 
              color="inherit"
              disabled={isAnalyzing}
              sx={{
                fontWeight: 500,
                textTransform: 'none',
                px: 2
              }}
            >
              Cancel
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              disabled={getSelectedImagesCount() === 0 || isAnalyzing}
              onClick={simulateAnalysis}
              startIcon={isAnalyzing ? 
                <AutoAwesomeIcon sx={{ animation: 'spinSparkle 2s linear infinite' }} /> : 
                <AutoAwesomeIcon />
              }
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                animation: isAnalyzing ? 'pulse 1.5s infinite' : 'none',
                '& .MuiSvgIcon-root': {
                  filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.5))'
                }
              }}
            >
              {isAnalyzing 
                ? `Analyzing ${getSelectedImagesCount()} Images...` 
                : `Analyze ${getSelectedImagesCount()} Selected Images`
              }
            </Button>
          </>
        ) : (
          // Normal buttons
          <>
            <Button 
              onClick={onClose} 
              color="inherit"
              sx={{
                fontWeight: 500,
                textTransform: 'none',
                px: 2
              }}
            >
              Close
            </Button>
            
            {tabValue === 1 && !reportView && !aiAnalysisView && (
              <Button
                variant="contained"
                color="primary"
                disabled={uploadFiles.length === 0 || uploading}
                onClick={handleUpload}
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2
                }}
              >
                {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} ${uploadFiles.length === 1 ? 'Photo' : 'Photos'}`}
              </Button>
            )}
            
            {reportView && !reportPreviewMode && (
              <>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Report Date"
                    value={reportDate}
                    onChange={(newDate) => setReportDate(newDate)}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: 180 }
                      }
                    }}
                  />
                </LocalizationProvider>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DescriptionIcon />}
                  onClick={handlePreviewDraftReport}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2
                  }}
                >
                  Preview Draft Finding
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DescriptionIcon />}
                  onClick={handlePreviewReport}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2
                  }}
                >
                  Preview PDF
                </Button>
              </>
            )}
            
            {reportView && reportPreviewMode && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<DescriptionIcon />}
                onClick={handleBackToReport}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2
                }}
              >
                Back to Report
              </Button>
            )}
          </>
        )}
      </DialogActions>
      
      {/* DELETE CONFIRMATION DIALOG - handles both single and bulk delete */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !bulkDeleting && closeDeleteConfirm()}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {photoToDelete ? 'Delete Photo' : `Delete ${getSelectedDeleteCount()} Photo${getSelectedDeleteCount() > 1 ? 's' : ''}`}
        </DialogTitle>
        <DialogContent>
          {photoToDelete ? (
            // Single photo delete
            <Typography id="delete-dialog-description">
              Are you sure you want to delete this photo?
              {photoToDelete?.has_annotation && (
                <Typography component="span" color="error" sx={{ display: 'block', mt: 1, fontWeight: 'medium' }}>
                  Warning: This photo contains annotations that will also be deleted!
                </Typography>
              )}
            </Typography>
          ) : (
            // Bulk delete
            <Box>
              <Typography id="delete-dialog-description">
                Are you sure you want to delete {getSelectedDeleteCount()} selected photo{getSelectedDeleteCount() > 1 ? 's' : ''}?
              </Typography>
              {/* Warning if any selected photos have annotations */}
              {Object.keys(selectedPhotosForDelete).filter(id =>
                selectedPhotosForDelete[id] && photos.find(p => p.id === parseInt(id))?.has_annotation
              ).length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Some selected photos contain annotations that will also be deleted!
                </Alert>
              )}
              {/* Progress indicator during bulk delete */}
              {bulkDeleting && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}
                    color="error"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Deleting {bulkDeleteProgress.current} of {bulkDeleteProgress.total}...
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteConfirm}
            color="inherit"
            disabled={deleting || bulkDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={photoToDelete ? handleDeletePhoto : handleBulkDelete}
            color="error"
            variant="contained"
            disabled={deleting || bulkDeleting}
            startIcon={(deleting || bulkDeleting) ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {(deleting || bulkDeleting) ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI ANNOTATION WARNING DIALOG */}
      <Dialog
        open={aiWarningDialogOpen}
        onClose={handleCancelAnalysis}
        aria-labelledby="ai-warning-dialog-title"
        aria-describedby="ai-warning-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle 
          id="ai-warning-dialog-title"
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            borderBottom: '1px solid',
            borderColor: theme.palette.warning.main
          }}
        >
          <WarningIcon color="warning" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Existing AI Annotations Detected
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Some images already have AI annotations
            </Typography>
            <Typography variant="body2">
              Running AI analysis again will <strong>delete and replace all existing AI annotations</strong> with new ones. 
              Manual annotations will remain untouched and safe.
            </Typography>
          </Alert>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            Images with existing AI annotations ({imagesWithAiAnnotations.length} total):
          </Typography>
          
          <Box sx={{ 
            maxHeight: 300, 
            overflowY: 'auto',
            border: '1px solid',
            borderColor: theme.palette.divider,
            borderRadius: 1,
            p: 1
          }}>
            <Grid container spacing={1}>
              {imagesWithAiAnnotations.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} key={photo.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.05)
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: theme.palette.grey[300],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        overflow: 'hidden'
                      }}
                    >
                      {photo.url ? (
                        <AuthenticatedImage
                          src={photo.url}
                          alt={`Photo ${photo.id}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <ImageIcon fontSize="small" />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 600, 
                        display: 'block',
                        color: 'text.primary'
                      }}>
                        Photo {photo.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        display: 'block',
                        fontSize: '0.7rem'
                      }}>
                        {photo.annotation_count} annotation{photo.annotation_count !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label="AI"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.6rem', 
                        height: 20,
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            💡 <strong>Tip:</strong> You can use "Run AI Analysis on Selected Images" to process only specific images 
            if you want to preserve some existing annotations.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button 
            onClick={handleCancelAnalysis} 
            color="inherit"
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleProceedWithAnalysis} 
            color="warning"
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              bgcolor: theme.palette.warning.main,
              '&:hover': {
                bgcolor: theme.palette.warning.dark,
              }
            }}
          >
            Proceed & Replace Annotations
          </Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};

export default InspectionDetailsModal; 